(() => {
  // The engine owns query order; the theme owns product and dropdown markup.
  // Failed or timed-out enhancements leave the native search path available.
  // API contract: cb-tools/docs/superpowers/specs/2026-08-20-storefront-search-rebuild-design.md
  "use strict";

  var TRACKING_KEYS = {
    gclid: 1, gbraid: 1, wbraid: 1, gad_source: 1, gad_campaignid: 1, dclid: 1,
    fbclid: 1, msclkid: 1, ttclid: 1, twclid: 1, li_fat_id: 1, srsltid: 1,
    mc_cid: 1, mc_eid: 1, _kx: 1, _ke: 1, klaviyo_id: 1, oseid: 1,
    ref: 1, _pos: 1, _sid: 1, _ss: 1, _psq: 1, _fid: 1
  };

  function isTrackingKey(key) {
    return key.indexOf("utm_") === 0 || Object.prototype.hasOwnProperty.call(TRACKING_KEYS, key);
  }

  function copyTrackingParams(target, source) {
    source.searchParams.forEach(function (value, key) {
      if (isTrackingKey(key)) target.searchParams.append(key, value);
    });
  }

  var PREDICTIVE_PROXY_PATH = "/a/search";
  var TIMEOUT_MS = 900; // L13 -- budget for the ENGINE query only. Warm suggest is ~250ms p95,
  // cold first requests 400-1000ms; the former 400ms limit caused a slower retry.
  var HEDGE_MS = 350; // send a duplicate typed-query suggest once the first leg is this late
  var DEBOUNCE_MS = 150;
  var SKU_DEBOUNCE_MS = 75; // strict catalog-number fast lane; generic text keeps DEBOUNCE_MS
  var STALL_MS = 300; // pending request threshold for the dropdown busy state
  var MIN_CHARS = 2;
  var SUGGEST_CACHE_TTL_MS = 45 * 1000;
  var SUGGEST_CACHE_MAX = 50;
  var directState = readDirectState();
  var ZERO_SETTLE_MS = 500; // only zero-result state waits for typing to settle
  var LEGACY_SEARCH_HEALTH_TIMEOUT_MS = 2500;
  var PROXY_HEALTH_FAILURE_TTL_MS = 3 * 1000;
  var DROPDOWN_IMAGE_WIDTHS = [180, 375, 540, 768, 920];
  var DROPDOWN_PLACEHOLDER_SRC =
    "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

  // ?sfs_debug=1 logs dropdown and request decisions.
  var DEBUG = /(?:^|[?&])sfs_debug=1(?:&|$)/.test(window.location.search);
  function dlog() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[sfs]");
    console.log.apply(console, args);
  }

  // ---------- small utilities ----------

  function engineSuggestEnabled() {
    return document.documentElement.getAttribute("data-sfs-engine-suggest") !== "off";
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function removeFirst(hay, needle) {
    if (!needle) return hay;
    var i = String(hay).indexOf(needle);
    if (i < 0) return hay;
    return hay.slice(0, i) + hay.slice(i + needle.length);
  }

  function shopifyWidthUrl(url, width) {
    var u = String(url || "");
    if (!u) return "";
    var shopFiles = /^(?:https?:)?\/\/cdn\.shopify\.com\/s\/files\/1\/0733\/6978\/0537\//;
    if (typeof location !== "undefined" && location && location.host && shopFiles.test(u)) {
      u = u.replace(shopFiles, "//" + location.host + "/cdn/shop/");
    } else {
      u = u.replace(/^https?:/, "");
    }
    u = u.replace(/([?&])width=\d+/g, "$1");
    u = u.replace(/\?&/g, "?").replace(/&&+/g, "&").replace(/[?&]$/, "");
    return u + (u.indexOf("?") >= 0 ? "&" : "?") + "width=" + width;
  }

  function dropdownVendor(product) {
    var brand = String(product.brand || "").trim();
    if (brand) return brand;
    var title = String(product.title || "");
    var parent = String(product.parent_brand || "").trim();
    var canonical = String(product.brand_canonical || "").trim();
    if (parent && title.indexOf(parent) !== -1) return parent;
    if (canonical && title.indexOf(canonical) !== -1) return canonical;
    return canonical || parent;
  }

  function dropdownRowModel(product) {
    var selected = product.default_variant;
    var defaultSku = selected && typeof selected.sku === "string" ? selected.sku.trim() : "";
    var sku = defaultSku || String(product.sku || "").trim();
    var href = "/products/" + String(product.handle || "");
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) href = window.SFSNativeFallback.presentHref(href);
    if (
      product.single_condition_option &&
      selected &&
      selected.available === false &&
      product.first_available_variant_id
    ) {
      var availId = String(product.first_available_variant_id);
      var variants = Array.isArray(product.variants) ? product.variants : [];
      var matched = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === availId) {
          matched = variants[i];
          break;
        }
      }
      if (matched) {
        if (matched.sku != null) sku = String(matched.sku).trim();
        var selectedParam = "variant=" + selected.id;
        var availableParam = "variant=" + availId;
        if (href.indexOf(selectedParam) !== -1) href = href.split(selectedParam).join(availableParam);
        else href += (href.indexOf("?") >= 0 ? "&" : "?") + availableParam;
      }
    }
    var skuBase = sku;
    if (sku.length >= 2 && sku.slice(-2).toLowerCase() === "-r") skuBase = sku.slice(0, -2);
    var title = String(product.title || "");
    var vendor = dropdownVendor(product);
    var description = title;
    if (vendor) {
      description = removeFirst(description, vendor).trim();
      var vendorShort = vendor.split("/")[0].trim();
      if (vendorShort && vendorShort !== vendor) description = removeFirst(description, vendorShort).trim();
    }
    if (sku) {
      var withoutSku = removeFirst(description, sku);
      if (withoutSku === description && skuBase !== sku) withoutSku = removeFirst(description, skuBase);
      if (withoutSku === description) {
        var compact = skuBase.replace(/[- ]/g, "");
        if (compact !== skuBase) withoutSku = removeFirst(description, compact);
      }
      description = withoutSku.trim();
    }
    var first = description.charAt(0);
    if (first === "–" || first === "—" || first === "-") description = description.slice(1).trim();
    if (!description) description = title;
    return {
      sku: sku,
      skuBase: skuBase,
      vendor: vendor,
      description: description,
      title: title,
      href: href,
      available: (product.default_variant && typeof product.default_variant.available === "boolean") ? String(product.default_variant.available) : (product.in_stock ? "true" : "false"),
      id: product.id == null ? "" : String(product.id),
      image: product.image || "",
    };
  }

  function dropdownImageHtml(model) {
    if (!model.image) {
      var fallback = typeof window !== "undefined" && window.theme && window.theme.assets && window.theme.assets.defaultProductImage;
      if (fallback) {
        return '<picture class="block image__style relative overflow-hidden" style="--imgHeight: 100%;">' +
          '<img src="' + esc(fallback) + '" class="absolute l0 t0 w100 h100 image-contain lazyloaded has-lazy" alt="Image coming soon" width="58" height="58" loading="lazy" decoding="async"></picture>';
      }
      return '<div class="image__style relative overflow-hidden" style="--imgHeight: 100%;"></div>';
    }
    var srcset = DROPDOWN_IMAGE_WIDTHS.map(function (w) {
      return esc(shopifyWidthUrl(model.image, w)) + " " + w + "w";
    }).join(", ");
    return (
      '<picture class="block image__style relative overflow-hidden" style="--imgHeight: 100%;">' +
      '<source media="all" sizes="58px" srcset="' +
      srcset +
      '"/>' +
      '<img src="' +
      DROPDOWN_PLACEHOLDER_SRC +
      '" class="absolute l0 t0 w100 h100 image-contain lazyloaded has-lazy" alt="' +
      esc(model.title) +
      '" sizes="58px" loading="lazy">' +
      "</picture>"
    );
  }

  function renderDropdownProductRow(product, progressive) {
    if (!product || !product.handle) return "";
    var model = dropdownRowModel(product);
    var copy;
    var merchandiseIds = window.theme && window.theme.merchandiseProductIds;
    var isMerchandise = Array.isArray(merchandiseIds) && merchandiseIds.indexOf(model.id) !== -1;
    if (!isMerchandise && Array.isArray(product.tags)) {
      isMerchandise = product.tags.some(function (tag) {
        return String(tag).trim().toLowerCase() === "merchandise";
      });
    }
    if (model.sku && !isMerchandise) {
      copy =
        '<span class="predictive-product-heading">' +
        (model.vendor ? '<span class="predictive-product-brand">' + esc(model.vendor) + "</span>" : "") +
        '<span class="predictive-product-sku">' +
        esc(model.skuBase) +
        "</span></span>" +
        '<span class="predictive-product-description">' +
        esc(model.description) +
        "</span>";
    } else {
      copy =
        '<span class="predictive-product-description predictive-product-description--primary">' +
        esc(model.title) +
        "</span>";
    }
    return (
      '<li role="option" class="relative" data-sku="' +
      esc(model.sku) +
      '" data-available="' +
      model.available +
      '" data-product-id="' +
      esc(model.id) +
      '"><a href="' +
      esc(model.href) +
      '" tabindex="-1" title="' +
      esc(model.title) +
      '" class="link-overlay"><div class="item-image-preview">' +
      dropdownImageHtml(model) +
      '</div><div class="item-detail"><div class="predictive-product-copy">' +
      copy +
      (progressive === true ? progressiveChangedFieldsHtml(product) : "") +
      "</div></div></a></li>"
    );
  }

  function isThemePreviewParam(name) {
    return name === "preview_theme_id" || name === "_fd" || name === "pb";
  }

  function isProxySearchPath(pathname) {
    return pathname === PREDICTIVE_PROXY_PATH || pathname === PREDICTIVE_PROXY_PATH + "/";
  }

  function isAllowedNativeSearchOption(name, value) {
    if (name === "options[prefix]") return value === "last" || value === "none";
    if (name === "options[unavailable_products]") {
      return value === "show" || value === "hide" || value === "last";
    }
    return false;
  }

  function hasValidSuggestionFacets(url) {
    var rawFacets = url.searchParams.getAll("facets");
    if (!rawFacets.length) return true;
    if (rawFacets.length !== 1) return false;

    var facets;
    try {
      facets = JSON.parse(rawFacets[0]);
    } catch (e) {
      return false;
    }
    if (!facets || typeof facets !== "object" || Array.isArray(facets)) return false;
    var keys = Object.keys(facets);
    if (!keys.length || keys.length > 3) return false;
    return keys.every(function (key) {
      var values = Array.isArray(facets[key]) ? facets[key] : [facets[key]];
      if (!values.length || values.length > 12) return false;
      return /^(?:brand_canonical|product_type|series)$/.test(key) && values.every(function (value) {
        return typeof value === "string" && !!value.trim() && value.length <= 200;
      });
    });
  }

  function hasExcludedLegacySearchState(url) {
    if (!url || url.pathname !== "/search" || url.hash || url.username || url.password) return true;

    var queries = url.searchParams.getAll("q");
    if (queries.length !== 1 || !queries[0].trim()) return true;
    if (url.searchParams.has("sfs_native")) return true;
    var types = url.searchParams.getAll("type");
    if (types.length > 1 || (types.length === 1 && types[0] !== "product")) {
      return true;
    }

    var eligible = true;
    var seen = { q: true, type: true };
    url.searchParams.forEach(function (value, key) {
      if (key === "q" || key === "type" || isTrackingKey(key)) return;
      if (isThemePreviewParam(key)) {
        if (seen[key]) eligible = false;
        seen[key] = true;
        return;
      }
      if (key.indexOf("options[") === 0) {
        if (seen[key] || !isAllowedNativeSearchOption(key, value)) eligible = false;
        seen[key] = true;
        return;
      }
      // Links from the retired SKU layer still live in bookmarks and browser
      // history (/search?q=...&highlight_product=<id>&highlight_handle=<handle>).
      // The proxy already reads highlight_product for its exact-match card, so
      // these hand off instead of stranding the shopper on native search.
      if (key === "highlight_product" || key === "highlight_handle") {
        var highlightPattern = key === "highlight_product" ? /^\d{1,20}$/ : /^[a-z0-9][a-z0-9-]{0,254}$/;
        if (seen[key] || !highlightPattern.test(value)) eligible = false;
        seen[key] = true;
        return;
      }
      eligible = false;
    });
    return !eligible;
  }

  function copyThemePreviewParams(target, source) {
    source.searchParams.forEach(function (value, key) {
      if (isThemePreviewParam(key)) target.searchParams.set(key, value);
    });
  }

  // Direct handoff intentionally narrows a plain legacy query to the product
  // catalog. Explicit non-product search state stays on native search.
  function normalizeLegacySearchHref(rawHref, baseHref) {
    try {
      var base = new URL(baseHref || window.location.href);
      var url = new URL(rawHref, base);
      if (url.origin !== base.origin || hasExcludedLegacySearchState(url)) return rawHref;
      url.pathname = PREDICTIVE_PROXY_PATH;
      copyThemePreviewParams(url, base);
      return url.pathname + url.search;
    } catch (e) {
      return rawHref;
    }
  }

  // Suggestions permit known pages or allowlisted proxy facet JSON.
  function normalizeSuggestionHref(rawHref, baseHref) {
    try {
      var base = new URL(baseHref || window.location.href);
      var url = new URL(rawHref, base);
      if (
        url.origin !== base.origin ||
        (url.protocol !== "http:" && url.protocol !== "https:") ||
        url.username ||
        url.password
      ) return "";

      if (url.pathname === "/search" || isProxySearchPath(url.pathname)) {
        if (url.hash) return "";
        var queries = url.searchParams.getAll("q");
        if (queries.length !== 1 || !queries[0].trim()) return "";
        var types = url.searchParams.getAll("type");
        if (types.length > 1 || (types.length === 1 && types[0] !== "product")) {
          return "";
        }
        if (!hasValidSuggestionFacets(url)) return "";

        var valid = true;
        url.searchParams.forEach(function (_value, key) {
          if (!/^(?:q|type|facets)$/.test(key) && !isTrackingKey(key)) valid = false;
        });
        if (!valid) return "";

        var target = new URL(PREDICTIVE_PROXY_PATH, base);
        target.searchParams.set("q", queries[0]);
        if (types.length === 1) target.searchParams.set("type", "product");
        var facets = url.searchParams.getAll("facets");
        if (facets.length === 1) target.searchParams.set("facets", facets[0]);
        copyTrackingParams(target, url);
        copyThemePreviewParams(target, base);
        return target.pathname + target.search;
      }

      if (!/^\/(?:products|collections|pages)\/[^/]+\/?$/.test(url.pathname)) return "";
      return url.pathname + url.search + url.hash;
    } catch (e) {
      return "";
    }
  }

  function nativeFallbackHrefFromProxy(rawHref, baseHref) {
    try {
      var base = new URL(baseHref || window.location.href);
      var source = new URL(rawHref, base);
      var queries = source.searchParams.getAll("q");
      if (
        source.origin !== base.origin ||
        !isProxySearchPath(source.pathname) ||
        source.username ||
        source.password ||
        queries.length !== 1 ||
        !queries[0].trim()
      ) return rawHref;

      var target = new URL("/search", base);
      target.searchParams.set("q", queries[0]);
      target.searchParams.set("type", "product");
      ["options[prefix]", "options[unavailable_products]"].forEach(function (key) {
        var values = source.searchParams.getAll(key);
        if (values.length === 1 && isAllowedNativeSearchOption(key, values[0])) {
          target.searchParams.set(key, values[0]);
        }
      });
      copyTrackingParams(target, source);
      copyThemePreviewParams(target, source);
      target.searchParams.set("sfs_native", "1");
      return target.pathname + target.search;
    } catch (e) {
      return rawHref;
    }
  }

  function isHealthySuggestionPayload(data) {
    return !!(
      data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      typeof data.intent === "string" &&
      data.intent.trim() &&
      Array.isArray(data.suggestions) &&
      Array.isArray(data.products) &&
      Object.prototype.hasOwnProperty.call(data, "pinned") &&
      !data.error
    );
  }

  // Reject partial 200 pages; valid result and zero states share these anchors.
  function proxyPageHasStableResultStructure(root) {
    return !!(
      root &&
      root.querySelector("filter-component.plp-shell .collection-main") &&
      (root.querySelector(".sfs-results-head .section-heading") ||
        root.querySelector(".sfs-results-head--normalized .section-heading") ||
        root.querySelector(".section-heading-wrapper .section-heading")) &&
      root.querySelector(".product-card, .sfs-empty")
    );
  }

  // Repair proxy facet IDs that collide after service-side normalization.
  function ensureUniqueProxyFilterControlIds() {
    if (!isProxySearchPath(window.location.pathname)) return 0;
    var root = document.getElementById("shopify-section-proxy-search");
    if (!root || !proxyPageHasStableResultStructure(root)) return 0;

    var seen = Object.create(null);
    var changed = 0;
    root.querySelectorAll(".filter-section-list input[id], .sort-section-list input[id]").forEach(function (control) {
      var oldId = control.id;
      if (!oldId) return;
      if (!seen[oldId]) {
        seen[oldId] = 1;
        return;
      }

      var nextIndex = seen[oldId] + 1;
      var nextId;
      do {
        nextId = oldId + "--" + String(nextIndex);
        nextIndex += 1;
      } while (document.getElementById(nextId));
      seen[oldId] = nextIndex - 1;

      var item = control.closest("li");
      if (item) {
        var labels = item.querySelectorAll("label[for]");
        for (var index = 0; index < labels.length; index += 1) {
          if (labels[index].getAttribute("for") !== oldId) continue;
          labels[index].setAttribute("for", nextId);
          break;
        }
      }
      control.id = nextId;
      changed += 1;
    });
    return changed;
  }

  function suggestionCountText(value) {
    if (typeof value !== "number" || !isFinite(value) || value < 0) return "";
    return " (" + String(value) + ")";
  }

  // Search analytics used to live in predictive-search.js. Keep it here so
  // retiring the competing dropdown controller does not remove query/click
  // measurement.
  var SEARCH_BEACON_URL = "/apps/sb/search/beacon";

  function searchBeaconUrl() {
    try {
      if (window.theme && window.theme.searchBeaconUrl) return window.theme.searchBeaconUrl;
    } catch (e) {}
    return SEARCH_BEACON_URL;
  }

  function isSearchBeaconSuppressed(themeState) {
    return !!(themeState && themeState.editor);
  }

  function isAnalyticsConsentDenied(customerPrivacy) {
    try {
      if (!customerPrivacy) return false;
      if (typeof customerPrivacy.analyticsProcessingAllowed === "function") {
        if (customerPrivacy.analyticsProcessingAllowed() === false) return true;
      } else if (typeof customerPrivacy.currentVisitorConsent === "function") {
        var consent = customerPrivacy.currentVisitorConsent();
        if (consent && consent.analytics === "no") return true;
      }
      if (typeof customerPrivacy.saleOfDataAllowed === "function" && customerPrivacy.saleOfDataAllowed() === false) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  function searchIdsBlocked() {
    try {
      if (typeof window.sbTrackingAllowed === "function") return window.sbTrackingAllowed() === false;
    } catch (e) {}
    try {
      return isAnalyticsConsentDenied(window.Shopify && window.Shopify.customerPrivacy);
    } catch (e2) {
      return false;
    }
  }

  function readSearchGate() {
    try {
      if (typeof window.sbTrackingAllowed === "function") return window.sbTrackingAllowed();
    } catch (e) {}
    try {
      if (typeof document !== "undefined" && document.prerendering) return false;
      if (window.theme && window.theme.editor) return false;
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== "function") return null;
      if (cp.analyticsProcessingAllowed() !== true) return false;
      if (typeof cp.saleOfDataAllowed === "function" && cp.saleOfDataAllowed() === false) return false;
      return true;
    } catch (e2) {
      return null;
    }
  }

  function etSessionDay(ms) {
    try {
      if (!etSessionDay.fmt) {
        etSessionDay.fmt = new Intl.DateTimeFormat("en-CA", {
          timeZone: "America/New_York",
          year: "numeric",
          month: "2-digit",
          day: "2-digit"
        });
      }
      return etSessionDay.fmt.format(new Date(ms));
    } catch (e) {
      return "";
    }
  }

  function getSearchSessionId() {
    var SESSION_MS = 30 * 60 * 1000;
    var WRITE_GAP = 60 * 1000;
    var sidOk = /^[A-Za-z0-9_-]{8,64}$/;
    try {
      if (searchIdsBlocked()) {
        try { localStorage.removeItem("sfs_session"); } catch (e0) {}
        try { localStorage.removeItem("sfs_first"); } catch (e0b) {}
        try { sessionStorage.removeItem("sfs_sid"); } catch (e0c) {}
        return "";
      }
      var now = Date.now();
      var saved = null;
      try { saved = JSON.parse(localStorage.getItem("sfs_session") || ""); } catch (e1) {}
      var id = saved && sidOk.test(saved.id) ? saved.id : "";
      var stamp = saved ? Number(saved.t || 0) : 0;
      var dayNow = etSessionDay(now);
      var dayThen = stamp ? etSessionDay(stamp) : "";
      var sameDay = !dayNow || !dayThen || dayNow === dayThen;
      var fresh = !!id && stamp > 0 && (now - stamp) < SESSION_MS && sameDay;
      if (!fresh) {
        id = "";
        try {
          if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            id = String(crypto.randomUUID()).replace(/-/g, "");
          }
        } catch (e2) {}
        if (!sidOk.test(id)) {
          id = "";
          while (id.length < 32) id += Math.random().toString(36).slice(2);
          id = id.slice(0, 32);
        }
        stamp = 0;
      }
      if (!fresh || (now - stamp) >= WRITE_GAP) {
        try { localStorage.setItem("sfs_session", JSON.stringify({ id: id, t: now })); } catch (e3) {}
      }
      try {
        if (sessionStorage.getItem("sfs_sid") !== id) sessionStorage.setItem("sfs_sid", id);
      } catch (e4) {}
      return id;
    } catch (e) {
      return "";
    }
  }

  function buildSearchBeaconPayload(q, action, results, page, position, productId) {
    var sid = "";
    try {
      sid = getSearchSessionId();
    } catch (e) {}
    try {
      if (q && action !== "atc") sessionStorage.setItem("sfs_last_q", q);
    } catch (e) {}
    return {
      q: q || "",
      action: action,
      results: typeof results === "number" ? results : -1,
      page: page || "",
      position: typeof position === "number" ? position : null,
      product_id: productId || null,
      sid: sid || null,
    };
  }

  // ---------- prerender-safe beacon gating ----------
  // Speculation Rules can prerender /a/search before a shopper ever commits
  // (see scheduleResultsPrerender below). A page that was only prerendered
  // and never activated must report zero pageview/search events, so every
  // beacon funnels through this one choke point -- sendSearchBeacon is the
  // single low-level sender behind both the local sendBeacon() wrapper and
  // window.theme.searchBeacon.send -- instead of being gated per call site.
  // document.prerendering is undefined in browsers without the API;
  // undefined is falsy, so isPrerendering() is false there and every beacon
  // fires exactly as it always has.
  var deferredBeacons = null; // null until the first deferral; then an array, drained once
  function isPrerendering() {
    return !!(typeof document !== "undefined" && document.prerendering);
  }
  function flushDeferredBeacons() {
    var queue = deferredBeacons;
    deferredBeacons = null;
    if (!queue) return;
    queue.forEach(function (fn) {
      fn();
    });
  }
  function runOrDeferBeacon(fn) {
    if (!isPrerendering()) {
      fn();
      return;
    }
    if (!deferredBeacons) {
      deferredBeacons = [];
      document.addEventListener("prerenderingchange", flushDeferredBeacons, { once: true });
    }
    deferredBeacons.push(fn);
  }

  var searchConsentQueue = [];
  var searchConsentDropped = false;
  var searchConsentBound = false;

  function clearSearchIdKeys() {
    try { localStorage.removeItem("sfs_session"); } catch (e) {}
    try { localStorage.removeItem("sfs_first"); } catch (e) {}
    try { sessionStorage.removeItem("sfs_sid"); } catch (e) {}
  }

  function deliverSearchBeacon(payload) {
    try {
      if (isSearchBeaconSuppressed(window.theme)) return;
      if (readSearchGate() !== true) return;
      var body = JSON.stringify(payload);
      var url = searchBeaconUrl();
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: "text/plain" });
        if (navigator.sendBeacon(url, blob)) return;
      }
      fetch(url, {
        method: "POST",
        body: body,
        keepalive: true,
        headers: { "Content-Type": "text/plain" },
      }).catch(function () {});
    } catch (e) {}
  }

  function flushSearchConsent() {
    try {
      var gate = readSearchGate();
      if (gate === false) {
        searchConsentQueue = [];
        searchConsentDropped = true;
        clearSearchIdKeys();
        return;
      }
      if (gate !== true) return;
      var waiting = searchConsentQueue;
      searchConsentQueue = [];
      var i;
      for (i = 0; i < waiting.length; i++) deliverSearchBeacon(waiting[i]);
    } catch (e) {}
  }

  function bindSearchConsent() {
    if (searchConsentBound) return;
    searchConsentBound = true;
    try {
      document.addEventListener("visitorConsentCollected", flushSearchConsent);
    } catch (e) {}
    try {
      if (window.sbTrackingAllowed && typeof window.sbTrackingAllowed.onChange === "function") {
        window.sbTrackingAllowed.onChange(flushSearchConsent);
      }
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], flushSearchConsent);
      }
    } catch (e) {}
    try {
      setTimeout(function () {
        try {
          searchConsentDropped = true;
          if (readSearchGate() !== true) searchConsentQueue = [];
        } catch (e2) {
          searchConsentDropped = true;
          searchConsentQueue = [];
        }
      }, 3000);
    } catch (e) {}
  }

  function sendSearchBeacon(payload) {
    runOrDeferBeacon(function () {
      try {
        if (isSearchBeaconSuppressed(window.theme)) return;
        try { bindSearchConsent(); } catch (eBind) {}
        var gate = readSearchGate();
        if (gate === false) return;
        if (gate !== true) {
          if (searchConsentDropped) return;
          if (searchConsentQueue.length < 30) searchConsentQueue.push(payload);
          return;
        }
        var body = JSON.stringify(payload);
        var url = searchBeaconUrl();
        if (navigator.sendBeacon) {
          var blob = new Blob([body], { type: "text/plain" });
          if (navigator.sendBeacon(url, blob)) return;
        }
        fetch(url, {
          method: "POST",
          body: body,
          keepalive: true,
          headers: { "Content-Type": "text/plain" },
        }).catch(function () {});
      } catch (e) {
        // Fire-and-forget. Analytics must never block search.
      }
    });
  }

  window.theme = window.theme || {};
  window.theme.searchBeacon = {
    send: sendSearchBeacon,
    buildPayload: buildSearchBeaconPayload,
    sessionId: getSearchSessionId,
    isSuppressed: isSearchBeaconSuppressed,
    isConsentDenied: isAnalyticsConsentDenied,
  };

  // Paint timing for dropdown/zero. Call sites stay 3-arg; render stashes the
  // diag here immediately before sendBeacon so older callers keep their shape.
  var beaconPaintDiag = null;

  function sendBeacon(q, action, results, position, productId) {
    var diag = arguments.length > 5 ? arguments[5] : null;
    try {
      if (window.SBAnalytics && action !== 'enter') {
        window.SBAnalytics.emit(action === 'click' ? 'sb_product_select' : 'sb_search_dropdown_view', {
          interaction_surface: 'search_dropdown', product_id: String(productId || ''),
          result_count: typeof results === 'number' ? results : -1,
          result_position: typeof position === 'number' ? position + 1 : 0
        });
      }
      if (!window.theme || !window.theme.searchBeacon || typeof window.theme.searchBeacon.send !== "function") return;
      if (!diag && (action === "dropdown" || action === "zero")) {
        try {
          diag = beaconPaintDiag;
          beaconPaintDiag = null;
        } catch (eDiag) {}
      }
      var payload = window.theme.searchBeacon.buildPayload(
        q,
        action,
        typeof results === "number" ? results : -1,
        (window.theme.routes && window.theme.routes.pageType) || (isSearchLandingPath() ? "search" : ""),
        typeof position === "number" ? position : null,
        productId || null
      );
      if (!payload || typeof payload !== "object") return;
      if (!payload.sid) {
        try { payload.sid = getSearchSessionId() || null; } catch (eSid) { payload.sid = null; }
      }
      try {
        if ((action === "dropdown" || action === "zero") && diag && typeof diag.startedAt === "number") {
          var ms = Math.round(Date.now() - diag.startedAt);
          if (isFinite(ms)) {
            if (ms < 0) ms = 0;
            if (ms > 60000) ms = 60000;
            payload.ms = ms;
          }
        }
      } catch (eMs) {}
      window.theme.searchBeacon.send(payload);
    } catch (e) {
      // fire-and-forget, never throw
    }
  }

  // Races the ENGINE query against TIMEOUT_MS (L13/L14). Every rejection is
  // handled the same way by callers: do nothing, native stands.
  function fetchWithTimeout(url, options, timeoutMs, signal) {
    var controller = new AbortController();
    var abortFromOwner = function () {
      controller.abort();
    };
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", abortFromOwner, { once: true });
    }
    var timer = setTimeout(function () {
      controller.abort();
    }, timeoutMs || TIMEOUT_MS);
    var init = Object.assign({}, options || {}, { signal: controller.signal });
    return fetch(url, init).finally(function () {
      clearTimeout(timer);
      if (signal) signal.removeEventListener("abort", abortFromOwner);
    });
  }

  function fetchJSON(url, timeoutMs, signal) {
    return fetchWithTimeout(url, { headers: { Accept: "application/json" } }, timeoutMs, signal)
      .then(function (res) {
        if (!res.ok) {
          var err = new Error("http " + res.status);
          err.status = res.status;
          if (res.headers && typeof res.headers.get === "function") {
            err.retryAfter = res.headers.get("Retry-After");
          }
          throw err;
        }
        // Non-enumerable so callers treating this as the plain payload
        // (JSON.stringify, caching) never see it -- only the diag code below
        // reads it explicitly.
        return res.json().then(function (json) {
          try {
            if (json && typeof json === "object") {
              Object.defineProperty(json, "__sfsHttpStatus", {
                value: res.status,
                enumerable: false,
                configurable: true,
              });
            }
          } catch (e) {}
          return json;
        });
      });
  }

  // 429 (rate limited) and 503 (unavailable) mean the engine itself is
  // overloaded -- the fix for W3.3 below never hedges or retries those
  // immediately, unlike a plain transport failure or a 500.
  function isOverloadStatus(status) {
    return status === 429 || status === 503;
  }

  // Retry-After per RFC 9110: either a delay in seconds or an HTTP-date.
  // Falls back to 2s plus up to 500ms of jitter so a page of shoppers who
  // all hit the same overload do not all retry in lockstep.
  function overloadBackoffMs(err) {
    var header = err && err.retryAfter;
    if (header) {
      var seconds = Number(header);
      if (isFinite(seconds) && seconds >= 0) return seconds * 1000;
      var when = Date.parse(header);
      if (!isNaN(when)) {
        var delta = when - Date.now();
        if (delta >= 0) return delta;
      }
    }
    return 2000 + Math.random() * 500;
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments,
        ctx = this;
      var delay = typeof ms === "function" ? ms.apply(ctx, args) : ms;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, delay);
    };
  }

  // Only accelerate a single catalog-number-shaped token. Broad text,
  // numeric searches, and multi-token fitment queries keep the established
  // 150 ms debounce so this change cannot increase their request rate.
  function isSkuLikeQuery(q) {
    q = String(q || "").trim();
    return q.length >= 5 && /^[A-Za-z0-9-]+$/.test(q) && /[A-Za-z]/.test(q) && /\d/.test(q);
  }

  function dedupeById(pinned, products) {
    var out = [];
    if (pinned) out.push(pinned);
    products.forEach(function (p) {
      if (pinned && p.id === pinned.id) return;
      out.push(p);
    });
    return out;
  }

  /** Render display-ready change labels supplied by the search planner. */
  function progressiveChangedFieldsHtml(product) {
    if (!product.match_group || !Array.isArray(product.changed_fields)) return "";
    const labels = [];
    for (const change of product.changed_fields) {
      if (!change || typeof change.label !== "string" || !change.label.trim()) continue;
      labels.push('<span class="sfs-progressive__change">' + esc(change.label) + '</span>');
    }
    return labels.length ? '<span class="sfs-progressive__changes">' + labels.join("") + '</span>' : "";
  }

  /** Group headings have no option role, links, or product positions. */
  function progressiveDropdownProductsHtml(products, plan) {
    const groups = new Map((Array.isArray(plan.groups) ? plan.groups : []).filter(Boolean).map(group => [group.id, group]));
    const displayed = new Set();
    const rows = [];
    for (const product of products) {
      const row = renderDropdownProductRow(product, true);
      if (!row) continue;
      const group = groups.get(product.match_group);
      if (group && !displayed.has(group.id)) {
        rows.push('<li role="presentation" class="sfs-progressive__group"><span role="heading" aria-level="3">' +
          esc(group.label || (group.strict ? 'Matches your search' : 'Broader results')) + '</span></li>');
        displayed.add(group.id);
      }
      rows.push(row);
    }
    return rows.join("");
  }

  /** Counts describe the fixed result plan, not the dropdown's ten-row prefix. */
  function progressiveDropdownSummaryHtml(plan) {
    if (!plan) return "";
    const count = value => Math.max(0, Number(value) || 0).toLocaleString('en-US');
    const parts = [count(plan.strict_count) + (Number(plan.strict_count) === 1 ? ' exact match' : ' exact matches') +
      (plan.count_is_exact === false ? ' found' : '')];
    for (const group of (Array.isArray(plan.groups) ? plan.groups : [])) {
      if (!group || group.strict || !group.loaded_count) continue;
      parts.push(count(group.loaded_count) + ' ' + String(group.label || 'broader results').toLowerCase() + ' loaded');
    }
    const notices = [];
    if ((Array.isArray(plan.groups) ? plan.groups : []).some(group => group && !group.strict &&
      Array.isArray(group.changed_fields) && group.changed_fields.some(field => field === 'series' || field === 'family'))) {
      notices.push('<span class="sfs-progressive__notice">Check panel fit for other series.</span>');
    }
    for (const [key, prefix] of [['unsupported', 'Not verified: '], ['conflicts', 'Conflicting requirements: ']]) {
      const details = [];
      for (const item of (Array.isArray(plan[key]) ? plan[key] : [])) {
        const label = typeof item === 'string' ? item : item && (item.message || item.label || item.field);
        if (label) details.push(String(label));
      }
      if (details.length) notices.push('<span class="sfs-progressive__notice">' + esc(prefix + details.join(', ')) + '</span>');
    }
    if (plan.bounded || plan.count_is_exact === false) notices.push('<span class="sfs-progressive__notice">Broader counts show loaded results, not the full catalog.</span>');
    return '<div class="sfs-progressive__summary">' + esc(parts.join('; ')) + '.' + notices.join('') + '</div>';
  }

  // ---------- native-markup builders (byte-identical to the theme's own
  // templates. Product rows come from renderDropdownProductRow above, which
  // ports templates/product.api-row.liquid. Sources cited inline.) ----------

  // sections/predictive-search.liquid lines 114-126 (queries rail group),
  // reused for our engine's family/brand/category suggestions.
  function suggestionRailHtml(suggestions) {
    if (!Array.isArray(suggestions) || !suggestions.length) return "";
    var items = suggestions
      .map(function (s) {
        if (!s || typeof s !== "object") return "";
        var href = normalizeSuggestionHref(s.url, window.location.href);
        if (!href) return "";
        return (
          '<li role="option"><a href="' +
          esc(href) +
          '" tabindex="-1"><p class="" aria-label="' +
          esc(s.label) +
          '">' +
          esc(s.label) +
          suggestionCountText(s.count) +
          "</p></a></li>"
        );
      })
      .join("");
    if (!items) return "";
    return (
      '<div class="predictive__search-rail">' +
      '<div class="predictive__search-group predictive-search-secondary">' +
      "<h3>Suggestions</h3>" +
      '<ul role="group" aria-labelledby="predictive-search-queries">' +
      items +
      "</ul></div></div>"
    );
  }

  function prefixSuggestHtml(suggestions) {
    var items = "";
    if (Array.isArray(suggestions) && suggestions.length) {
      items = suggestions
        .map(function (s) {
          if (!s || typeof s !== "object") return "";
          var href = normalizeSuggestionHref(s.url, window.location.href);
          if (href && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) href = window.SFSNativeFallback.presentHref(href);
          if (!href) return "";
          return (
            '<li role="option"><a href="' +
            esc(href) +
            '" tabindex="-1"><p class="" aria-label="' +
            esc(s.label) +
            '">' +
            esc(s.label) +
            suggestionCountText(s.count) +
            "</p></a></li>"
          );
        })
        .join("");
    }
    return (
      '<div class="predictive__search-result empty-hidden">' +
      '<p class="sfs-prefix-hint">Keep typing a part number, or pick a family or brand.</p>' +
      (items
        ? '<div class="predictive__search-group predictive-search-secondary sfs-prefix-chips">' +
          "<h3>Suggestions</h3>" +
          '<ul role="group" aria-labelledby="predictive-search-queries">' +
          items +
          "</ul></div>"
        : "") +
      "</div>"
    );
  }

  // Item 1: rail links often land on a collection/brand page, not
  // /a/search, so the landing hook never sees them.
  function wireRailRecent(panel, q) {
    panel.querySelectorAll(".predictive__search-rail a").forEach(function (a) {
      a.addEventListener("click", function () {
        recordRecentSearch(q);
      });
    });
  }

  // sections/predictive-search.liquid lines 193-205, reused verbatim except
  // the destination path: Phase 2 app-proxy cutover (2026-08-21 spec) moves
  // this dropdown commit -- the "View all" / "Search for ..." click -- onto
  // the app-proxy delivery lane. Same param logic as before (type/options),
  // only the leading path segment changed; Enter-key commits get there too,
  // via the header/mobile form's own action (snippets/form.liquid).
  function viewAllButtonHtml(q) {
    var href = '/a/search?q=' + encodeURIComponent(q) +
      '&type=product&options%5Bunavailable_products%5D=last&options%5Bprefix%5D=last&sfs_cards=liquid';
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) href = window.SFSNativeFallback.presentHref(href);
    return (
      '<div class="predictive_search-button">' +
      '<a href="' + esc(href) + '" class="c-pointer predictive-search-action" tabindex="-1" role="option" aria-selected="false">' +
      '<span class="predictive-search-button-default">Search for &ldquo;' +
      esc(q) +
      "&rdquo;</span>" +
      '<span class="predictive-search-button-mobile">View all results for &ldquo;' +
      esc(q) +
      "&rdquo;</span>" +
      '<svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.51634 0.13651L7.84379 2.17044C8.05207 2.35245 8.05207 2.64755 7.84379 2.82956L5.51634 4.86349C5.30806 5.0455 4.97038 5.0455 4.7621 4.86349C4.55382 4.68148 4.55382 4.38638 4.7621 4.20436L6.17909 2.96607H0V2.03393H6.17909L4.7621 0.795637C4.55382 0.613624 4.55382 0.318523 4.7621 0.13651C4.97038 -0.0455033 5.30806 -0.0455033 5.51634 0.13651Z" fill="#808080"/></svg>' +
      "</a></div>"
    );
  }

  // sections/predictive-search.liquid lines 218-229, reused verbatim.
  function noResultHtml(q) {
    return (
      '<p class="predictive__search--no-result" style="display:block">' +
      '<span class="predictive__no-result-text">No result found for your search.</span>' +
      (q
        ? '<a class="predictive__no-result-quote" href="/pages/get-a-quote?q=' + encodeURIComponent(q) + '" tabindex="-1" role="option">Request a quote for ' + esc(q) + "</a>"
        : '<a class="predictive__no-result-quote" href="/pages/get-a-quote" tabindex="-1" role="option">Request a quote</a>') +
      "</p>"
    );
  }

  // snippets/svg.liquid 'checkbox' / 'checkbox-checked', reused verbatim --
  // both always render; CSS shows/hides by :checked, same as native.
  var CHECKBOX_SVG =
    '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 0H2C0.89 0 0 0.89 0 2V16C0 16.5304 0.210714 17.0391 0.585786 17.4142C0.960859 17.7893 1.46957 18 2 18H16C16.5304 18 17.0391 17.7893 17.4142 17.4142C17.7893 17.0391 18 16.5304 18 16V2C18 1.46957 17.7893 0.960859 17.4142 0.585786C17.0391 0.210714 16.5304 0 16 0ZM16 2V16H2V2H16Z" fill="#242427"/></svg>';
  var CHECKBOX_CHECKED_SVG =
    '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 0H2C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2V16C0 16.5304 0.210714 17.0391 0.585786 17.4142C0.960859 17.7893 1.46957 18 2 18H16C16.5304 18 17.0391 17.7893 17.4142 17.4142C17.7893 17.0391 18 16.5304 18 16V2C18 1.46957 17.7893 0.960859 17.4142 0.585786C17.0391 0.210714 16.5304 0 16 0ZM16 2V16H2V2H16ZM7 14L3 10L4.41 8.58L7 11.17L13.59 4.58L15 6" fill="#242427"/></svg>';
  var ARROW_SVG =
    '<svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 7L7 1L13 7" stroke="#242427" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // snippets/filter-item.liquid, reused verbatim per facet value.
  function filterItemHtml(groupKey, value, count, active) {
    var inputId = "sfsFacet-" + groupKey + "-" + value.replace(/[^a-z0-9]/gi, "");
    return (
      '<li class="filter-section-list-item relative jsFilterToggle">' +
      '<input type="checkbox" id="' +
      esc(inputId) +
      '" class="input-hidden filter-input" name="' +
      esc(groupKey) +
      '" value="' +
      esc(value) +
      '" data-facet-key="' +
      esc(groupKey) +
      '" data-facet-value="' +
      esc(value) +
      '"' +
      (active ? " checked" : "") +
      ">" +
      '<label for="' +
      esc(inputId) +
      '" class="c-pointer checkbox flex flex-gap">' +
      '<span class="input-state-default">' +
      CHECKBOX_SVG +
      "</span>" +
      '<span class="input-state-active">' +
      CHECKBOX_CHECKED_SVG +
      "</span>" +
      '<span class="text">' +
      esc(value) +
      "</span>" +
      '<span class="count" data-count="' +
      count +
      '"></span>' +
      "</label></li>"
    );
  }

  // snippets/sidebar-section.liquid accordion-component wrapper, reused
  // verbatim per facet group.
  function filterBlockHtml(groupKey, title, values, activeValue) {
    var items = values
      .map(function (v) {
        return filterItemHtml(groupKey, v.value, v.count, v.value === activeValue);
      })
      .join("");
    return (
      '<accordion-component class="filter-block filter-type- select-none ' +
      esc(groupKey) +
      '">' +
      "<details open><summary class=\"c-pointer\"><span class=\"label\">" +
      esc(title) +
      '</span><span class="expand">' +
      ARROW_SVG +
      '</span></summary>' +
      '<div class="filter-section-wrapper jsFilterUpdate" data-filter-id="' +
      esc(groupKey) +
      '"><ul class="filter-section-list" role="list">' +
      items +
      "</ul></div></details></accordion-component>"
    );
  }

  // snippets/sidebar-section.liquid active-filter chip list (lines ~296-302),
  // reused verbatim for our active facets.
  function activeFilterHtml(facets) {
    var keys = Object.keys(facets);
    if (!keys.length) return "";
    var items = keys
      .map(function (k) {
        return (
          '<li><a href="#" class="jsFilterRemove" data-facet-key="' +
          esc(k) +
          '"><span class="jsValue" hidden>' +
          esc(facets[k]) +
          '</span><span class="jsDisplayLabel">' +
          esc(facets[k]) +
          '</span><span class="icon">&times;</span></a></li>'
        );
      })
      .join("");
    return (
      '<div class="filter-active jsFilterUpdate">' +
      '<div class="filter-active-header flex justify-content-between"><p class="filter-active-title">' +
      keys.length +
      " filters applied</p></div>" +
      '<div class="filter-active-list"><ul class="flex flex-gap" role="list">' +
      items +
      "</ul></div></div>"
    );
  }

  // ---------- empty-state: recent searches + Popular curation (2026-08-21,
  // spec 2026-08-21-search-empty-state-polish-design.md) ----------

  var RECENT_KEY = "sfs_recent_searches";
  var RECENT_MAX = 5;

  // Guarded per spec ("Guard all storage access in try/catch (private
  // browsing)"): corrupt/missing/blocked storage degrades to an empty list,
  // never throws.
  function getRecentSearches() {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function setRecentSearches(list) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {
      // quota exceeded / private mode / storage disabled -- degrade
      // silently, the shopper just doesn't get a persisted history this tab
    }
  }

  // Case-insensitive dedupe, newest first, capped at RECENT_MAX. The active
  // app-proxy landing hook below calls this only after a committed search.
  function recordRecentSearch(q) {
    q = String(q || "").trim();
    if (!q) return;
    var list = getRecentSearches().filter(function (r) {
      return r && typeof r.q === "string" && r.q.toLowerCase() !== q.toLowerCase();
    });
    list.unshift({ q: q, ts: Date.now() });
    setRecentSearches(list.slice(0, RECENT_MAX));
  }

  function clearRecentSearches() {
    setRecentSearches([]);
  }

  // Recent Searches markup + wiring, shared by renderEmptyState and
  // paintInterimPanel below.
  function recentSearchesGroupHtml(recent) {
    var items = recent
      .slice(0, RECENT_MAX)
      .map(function (r) {
        return '<li role="option"><a href="#" class="sfs-recent-row" data-q="' + esc(r.q) + '" tabindex="-1"><p class="">' + esc(r.q) + "</p></a></li>";
      })
      .join("");
    return (
      '<div class="predictive__search-group sfs-empty-group sfs-recent-group">' +
      '<h3><span class="sfs-empty-title">Recent Searches</span><a href="#" class="sfs-recent-clear" tabindex="0">Clear</a></h3>' +
      '<ul role="group">' +
      items +
      "</ul></div>"
    );
  }

  function wireRecentRows(panel, self, onClear) {
    var clearLink = panel.querySelector(".sfs-recent-clear");
    if (clearLink) {
      clearLink.addEventListener("click", function (e) {
        e.preventDefault();
        clearRecentSearches();
        // Keep focus inside the search container before removing the Clear link.
        if (document.activeElement === clearLink) self.input.focus({ preventScroll: true });
        onClear();
      });
    }
    panel.querySelectorAll(".sfs-recent-row").forEach(function (a) {
      a.addEventListener("click", function (e) {
        e.preventDefault();
        self.runQuery(a.getAttribute("data-q"));
      });
    });
  }

  // Drops any Popular row that case-insensitively matches a Brands-section
  // name (the "siemens" dup Sagar flagged, from the query-string era) and
  // caps at 6 -- the engine may send more than that.
  //
  // 2026-08-22: the engine started filling `popular` with a revenue-ranked
  // AMPERAGE roll-up, replacing the old product-type source.
  //
  // 2026-08-23 correction: `popular` is no longer amperage-only -- the
  // engine now sends a revenue-ranked MIXED list spanning amperage, poles,
  // mounting style, and top non-breaker product types (e.g. "100 Amp",
  // "2 Pole", "Bolt-On", "Contactors"), and `brands` grew from 5 single
  // vendors to 6 merged groups. A value from either list colliding with the
  // other is still effectively impossible, so the dedupe stays exactly as
  // it was. Cap raised from 5 to 6 to show the full new list, matching the
  // group() builder's own 6-row cut (point 3 below).
  function curatePopular(popular, brands) {
    if (!popular || !popular.length) return [];
    var brandNames = {};
    (brands || []).forEach(function (b) {
      if (b && b.value) brandNames[String(b.value).toLowerCase()] = true;
    });
    var out = [];
    for (var i = 0; i < popular.length && out.length < 6; i++) {
      var item = popular[i];
      if (!item || !item.value) continue;
      if (brandNames[String(item.value).toLowerCase()]) continue;
      out.push(item);
    }
    return out;
  }

  // Bottom teaching row (AMENDMENT, Sagar 2026-08-21 ~15:5x EDT: "teaching
  // tip row" -- spec 2026-08-21-search-empty-state-polish-design.md).
  // Replaces the old static "Try a part number, e.g. QO120" line with a
  // short lead-in plus 3 fixed example chips. The 3 queries are literal
  // strings, not engine data, so there's no display-vs-run split to manage
  // -- the label IS the query, always (same posture every group() row takes
  // now that Popular Categories also renders verbatim -- see 2026-08-22/
  // 2026-08-23 notes above group() below).
  // Wired up the same way as Recent rows below (SfsDropdown.prototype.
  // runQuery -- fills the input and submits so the interceptor and
  // analytics see it exactly like a shopper typing it).
  var TIP_EXAMPLE_QUERIES = [
    "QO120",
    "20A 3 Pole",
    "Eaton BR 20A",
    "20A QO Breakers",
    "QO120 Square D QO Breakers",
  ];

  // Locked empty-state categories (Sagar 2026-09-01): Brands first, then
  // the five catalog destinations requested for this panel.
  // These use the same routes as the homepage catalog. The breaker label is
  // intentionally broad even though its established destination retains the
  // legacy 1-pole collection handle.
  var DROPDOWN_CATS = [
    { value: "Circuit Breakers", url: "/collections/circuit-breakers" },
    { value: "Contactors", url: "/collections/contactors" },
    { value: "Starters", url: "/collections/starters" },
    { value: "Overload Relays", url: "/collections/overload-relays" },
    { value: "Push Buttons", url: "/collections/pushbuttons" },
  ];
  // atTop (Sagar 2026-08-24): true when there is no Recent Searches row --
  // the tip line then renders above Popular Brands/Popular Categories
  // instead of its usual place below them, so a first-time or cleared-
  // history shopper sees it right under the input. Default/false keeps
  // today's plain sfs-tip-line class (bottom placement).
  function tipTeachHtml(atTop) {
    var chips = TIP_EXAMPLE_QUERIES.map(function (q) {
      return '<a href="#" class="sfs-tip-chip" data-q="' + esc(q) + '" tabindex="-1" role="option">' + esc(q) + "</a>";
    }).join("");
    return (
      '<div class="sfs-tip-line' + (atTop ? " sfs-tip-line--top" : "") + '">' +
      '<p class="sfs-tip-lead">Example Searches</p>' +
      '<div class="sfs-tip-chips">' +
      chips +
      "</div></div>"
    );
  }

  // ================= DROPDOWN (Lane B2) =================

  var suggestionCache = new Map();
  function suggestionCacheKey(q) {
    return String(q || "").trim().replace(/\s+/g, " ").toLowerCase();
  }

  function proxyHealthCacheKey(url) {
    var query = suggestionCacheKey(url.searchParams.get("q"));
    var themeId = url.searchParams.get("preview_theme_id") || "live";
    return "sfs_proxy_health_v2:" + encodeURIComponent(query) + ":" + encodeURIComponent(themeId);
  }

  function readCachedProxyHealth(key) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(key) || "null");
      if (!cached || cached.ok !== false || typeof cached.ts !== "number") {
        if (cached) sessionStorage.removeItem(key);
        return null;
      }
      if (Date.now() - cached.ts <= PROXY_HEALTH_FAILURE_TTL_MS) return false;
      sessionStorage.removeItem(key);
    } catch (e) {
      // Ignore unavailable or corrupt session storage.
    }
    return null;
  }

  function writeCachedProxyHealth(key, ok) {
    try {
      if (ok) {
        sessionStorage.removeItem(key);
        return;
      }
      sessionStorage.setItem(key, JSON.stringify({ ok: false, ts: Date.now() }));
    } catch (e) {}
  }

  // Journey beacon for a failed old-page hand-off health check. Skip when the
  // journey script is absent; never await or delay the page for logging.
  function recordLegacySearchHandoffFallback(fields) {
    try {
      if (!window.SBJourney || typeof window.SBJourney.track !== "function") return;
      var payload = {
        surface: "search_handoff",
        extra: {
          kind: "search_handoff_fallback",
          elapsed_ms: fields.elapsed_ms,
          reason: fields.reason,
          q_len: fields.q_len,
        },
      };
      if (fields.http_status != null && isFinite(Number(fields.http_status))) {
        payload.status = Math.max(0, Math.round(Number(fields.http_status)));
        payload.extra.http_status = payload.status;
      }
      window.SBJourney.track("error", payload);
    } catch (e) {}
  }

  function probeProxySuggestHealth(q, currentUrl) {
    var startedAt = Date.now();
    var qLen = String(q == null ? "" : q).length;
    var controller = new AbortController();
    var timedOut = false;
    var settled = false;
    var url = new URL(PREDICTIVE_PROXY_PATH, currentUrl.origin);
    url.searchParams.set("sfs_endpoint", "suggest");
    url.searchParams.set("q", q);
    copyThemePreviewParams(url, currentUrl);

    function fail(reason, httpStatus) {
      if (settled) return false;
      settled = true;
      recordLegacySearchHandoffFallback({
        elapsed_ms: Math.max(0, Date.now() - startedAt),
        reason: reason,
        http_status: httpStatus,
        q_len: qLen,
      });
      return false;
    }

    var timer = setTimeout(function () {
      timedOut = true;
      controller.abort();
    }, LEGACY_SEARCH_HEALTH_TIMEOUT_MS);
    return fetch(url.pathname + url.search, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    }).then(function (response) {
      if (settled) return false;
      if (!response.ok) return fail("http", response.status);
      if (!response.headers || !response.headers.get) return fail("not_json", response.status);
      var contentType = (response.headers.get("content-type") || "").toLowerCase();
      if (contentType.indexOf("application/json") === -1) {
        return fail("not_json", response.status);
      }
      return response.json().then(function (payload) {
        if (settled) return false;
        if (!isHealthySuggestionPayload(payload)) return fail("bad_payload", response.status);
        settled = true;
        return true;
      }).catch(function () {
        return fail("not_json", response.status);
      });
    }).catch(function (err) {
      if (settled) return false;
      if (timedOut || (err && err.name === "AbortError")) return fail("timeout");
      return fail("network");
    }).finally(function () {
      clearTimeout(timer);
    });
  }

  function initLegacySearchHandoff() {
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) return;
    var current;
    try {
      current = new URL(window.location.href);
    } catch (e) {
      return;
    }
    var destination = normalizeLegacySearchHref(window.location.href, window.location.href);
    if (destination === window.location.href) return;
    destination = preparePublicFilterHref(destination);

    var cacheKey = proxyHealthCacheKey(current);
    var cached = readCachedProxyHealth(cacheKey);
    if (cached !== null) {
      if (cached) window.location.replace(destination);
      return;
    }

    var landing = current.pathname + current.search + current.hash;
    probeProxySuggestHealth(current.searchParams.get("q"), current).then(function (healthy) {
      if (!healthy) {
        writeCachedProxyHealth(cacheKey, false);
        return;
      }
      var now;
      try {
        now = new URL(window.location.href);
      } catch (e) {
        return;
      }
      if (now.pathname + now.search + now.hash !== landing) return;
      window.location.replace(destination);
    });
  }

  function predictiveProxyUrl(q) {
    var url = new URL(PREDICTIVE_PROXY_PATH, window.location.origin);
    url.searchParams.set("sfs_endpoint", "suggest");
    url.searchParams.set("q", q);
    return url.pathname + url.search;
  }

  function directTokenState(value) {
    if (!value || value.base !== "https://search.superbreakers.com" ||
        typeof value.token !== "string" || !value.token ||
        typeof value.exp !== "number" || !isFinite(value.exp)) return null;
    return { base: value.base, token: value.token, exp: value.exp };
  }

  function readDirectState() {
    try {
      return directTokenState(JSON.parse(sessionStorage.getItem("sfs_direct_v1") || "null"));
    } catch (e) {
      return null;
    }
  }

  function captureDirect(data) {
    var next = directTokenState(data && data.direct);
    if (!next || (directState && next.exp <= directState.exp)) return;
    directState = next;
    try {
      sessionStorage.setItem("sfs_direct_v1", JSON.stringify(directState));
    } catch (e) {}
  }

  function forgetDirectState() {
    directState = null;
    try {
      sessionStorage.removeItem("sfs_direct_v1");
    } catch (e) {}
  }

  function directUsable() {
    return !!(directState && directState.exp - Date.now() / 1000 > 60);
  }

  function directSuggestUrl(q) {
    return directState.base + "/suggest?q=" + encodeURIComponent(q) + "&t=" + encodeURIComponent(directState.token);
  }

  // True once any /suggest request has left this page, typed or empty-query.
  var suggestRequested = false;

  // ---------- per-query suggest diagnostics (fallback-rate measurement) ----
  // One record per onInput() keystroke cycle: every leg it touches (direct/
  // proxy, hedge, the one retry, native fallback). Local-only, never a
  // beacon -- cb-tools' /api/search/beacon 400s an action outside its fixed
  // enum (dropdown/enter/click/redirect/zero). Read via window.__sfsSuggestDiag()
  // or the sfs_debug=1 console line below. A superseded/abandoned request is
  // never finalized, matching sendBeacon's own current-request-only gate.
  var SUGGEST_DIAG_KEY = "sfs_suggest_diag";
  var SUGGEST_DIAG_MAX = 20;

  function readSuggestDiagLog() {
    try {
      var parsed = JSON.parse(sessionStorage.getItem(SUGGEST_DIAG_KEY) || "null");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function pushSuggestDiagRecord(record) {
    try {
      var list = readSuggestDiagLog();
      list.push(record);
      while (list.length > SUGGEST_DIAG_MAX) list.shift();
      sessionStorage.setItem(SUGGEST_DIAG_KEY, JSON.stringify(list));
    } catch (e) {
      // quota exceeded / private mode -- degrade silently, same guard as
      // the recent-searches log above.
    }
    dlog("suggest diag", record.outcome, record.total_ms + "ms", record.legs.length + " leg(s)");
  }

  window.__sfsSuggestDiag = function () {
    return readSuggestDiagLog();
  };

  // Resource Timing entry for one leg's URL when the browser exposes one
  // (cross-origin dns/connect/tls/ttfb need Timing-Allow-Origin from the
  // target host, else they fall back to 0). Falls back to a Date.now() delta
  // for `ms` with no entry at all.
  function perfTimingForUrl(url, startedAt) {
    var timing = { ms: null, dns: 0, connect: 0, tls: 0, ttfb: 0 };
    try {
      if (typeof performance !== "undefined" && typeof performance.getEntriesByName === "function") {
        var absolute = new URL(url, window.location.href).href;
        var entries = performance.getEntriesByName(absolute, "resource");
        var entry = entries.length ? entries[entries.length - 1] : null;
        if (entry) {
          if (typeof entry.responseEnd === "number" && typeof entry.startTime === "number") {
            timing.ms = Math.max(0, entry.responseEnd - entry.startTime);
          }
          timing.dns = Math.max(0, (entry.domainLookupEnd || 0) - (entry.domainLookupStart || 0));
          timing.connect = Math.max(0, (entry.connectEnd || 0) - (entry.connectStart || 0));
          timing.tls = entry.secureConnectionStart > 0 ? Math.max(0, (entry.connectEnd || 0) - entry.secureConnectionStart) : 0;
          timing.ttfb = Math.max(0, (entry.responseStart || 0) - (entry.requestStart || 0));
        }
      }
    } catch (e) {}
    if (timing.ms == null) timing.ms = Date.now() - startedAt;
    timing.ms = Math.round(timing.ms);
    timing.dns = Math.round(timing.dns);
    timing.connect = Math.round(timing.connect);
    timing.tls = Math.round(timing.tls);
    timing.ttfb = Math.round(timing.ttfb);
    return timing;
  }

  // Reason codes: timeout, http_<status>, network, token, aborted.
  // ownerAborted separates a real timeout (fetchWithTimeout's own internal
  // controller fired) from a supersede (the caller's own signal aborted
  // first) -- both surface as the same AbortError from fetch().
  function classifySuggestFailure(err, isDirect, ownerAborted) {
    var status = err && typeof err.status === "number" ? err.status : null;
    if (status != null) {
      if (isDirect && (status === 401 || status === 403)) return { status: status, reason: "token" };
      return { status: status, reason: "http_" + status };
    }
    if (ownerAborted) return { status: null, reason: "aborted" };
    if (err && err.name === "AbortError") return { status: null, reason: "timeout" };
    return { status: null, reason: "network" };
  }

  function recordSuggestLeg(diag, legName, url, startedAt, status, reason) {
    if (!diag) return;
    var timing = perfTimingForUrl(url, startedAt);
    diag.legs.push({
      leg: legName,
      ms: timing.ms,
      status: status == null ? null : status,
      reason: reason || null,
      dns: timing.dns,
      connect: timing.connect,
      tls: timing.tls,
      ttfb: timing.ttfb,
    });
  }

  // Persists once per keystroke cycle. Skips a cache-served query (zero
  // legs -- nothing was attempted, nothing to measure) and a cycle already
  // finalized (the overload race below can reach this from two completion
  // points; the first to settle the visible outcome wins the record).
  function finalizeSuggestDiag(diag) {
    if (!diag || diag.finalized) return;
    if (!diag.legs.length) return;
    diag.finalized = true;
    var outcome = "none";
    for (var i = 0; i < diag.legs.length; i++) {
      if (diag.legs[i].reason === null) {
        outcome = diag.legs[i].leg;
        break;
      }
    }
    pushSuggestDiagRecord({
      q_len: diag.q_len,
      legs: diag.legs,
      outcome: outcome,
      total_ms: Date.now() - diag.startedAt,
    });
  }

  // Hedged suggest, measured 2026-09-05: through the app proxy a typed suggest
  // answers in 180-250 ms at p90, but 1-4% of requests stall 1-7 s inside
  // Shopify while the engine behind it holds 80 ms p95 -- so once the first leg
  // is hedgeMs late, send one proxy hedge and keep whichever answers first.
  function fetchSuggestHedged(legs, signal, hedgeMs, diag) {
    if (typeof legs === "string") legs = [legs, legs];
    return new Promise(function (resolve, reject) {
      var requests = [];
      var pending = 0;
      var done = false;
      var hedgeTimer = null;

      function settle(fn, value, winner) {
        if (done) return;
        done = true;
        clearTimeout(hedgeTimer);
        requests.forEach(function (leg) {
          if (signal) signal.removeEventListener("abort", leg.abortFromOwner);
          if (leg !== winner) leg.abort(); // the loser stops the moment a reply lands
        });
        fn(value);
      }

      function startLeg(index, budgetMs) {
        var url = legs[index];
        var isDirect = url.indexOf("https://search.superbreakers.com/suggest?") === 0;
        var leg = new AbortController();
        var legStartedAt = Date.now();
        leg.abortFromOwner = function () {
          leg.abort();
        };
        requests.push(leg);
        pending++;
        if (signal) {
          if (signal.aborted) leg.abort();
          else signal.addEventListener("abort", leg.abortFromOwner, { once: true });
        }
        if (index === 0 && isDirect) dlog("engine /suggest direct");
        fetchJSON(url, budgetMs, leg.signal).then(
          function (data) {
            if (diag) {
              recordSuggestLeg(
                diag,
                isDirect ? "direct" : "proxy",
                url,
                legStartedAt,
                data && data.__sfsHttpStatus != null ? data.__sfsHttpStatus : null,
                null
              );
            }
            settle(resolve, data, leg);
          },
          function (err) {
            if (done) return;
            dlog("engine /suggest FAIL", isDirect ? "direct" : "proxy");
            if (diag) {
              var classified = classifySuggestFailure(err, isDirect, leg.signal.aborted);
              recordSuggestLeg(diag, isDirect ? "direct" : "proxy", url, legStartedAt, classified.status, classified.reason);
            }
            if (isDirect && err && (err.status === 401 || err.status === 403)) forgetDirectState();
            pending--;
            // A fast direct error needs the proxy in this attempt, before a
            // retry -- unless the engine is signaling overload (429/503),
            // where adding a second concurrent request only makes the
            // overload worse. runSuggest's own backoff handles that case.
            if (
              index === 0 &&
              hedgeMs &&
              legs[1] !== legs[0] &&
              !isOverloadStatus(err && err.status) &&
              startHedge(TIMEOUT_MS)
            ) {
              return;
            }
            // Only the last leg standing may fail the whole attempt.
            if (pending === 0) settle(reject, err, leg);
          }
        );
      }

      function startHedge(budgetMs) {
        if (done || requests.length > 1 || (signal && signal.aborted)) return false;
        clearTimeout(hedgeTimer);
        dlog(legs[0] === legs[1] ? "engine /suggest hedge" : "engine /suggest hedge proxy");
        startLeg(1, budgetMs);
        return true;
      }

      startLeg(0, TIMEOUT_MS);
      if (!hedgeMs) return; // empty-query warm-up and focus requests never hedge
      hedgeTimer = setTimeout(function () {
        // One hedge per query at most, never once answered, already failed, or superseded.
        if (pending === 0) return;
        startHedge(TIMEOUT_MS - hedgeMs); // only the budget leg 1 has left, so the pair still fits 900 ms
      }, hedgeMs);
    });
  }

  function getSuggestion(q, signal, diag) {
    var key = suggestionCacheKey(q);
    var cached = suggestionCache.get(key);
    if (cached && Date.now() - cached.ts <= SUGGEST_CACHE_TTL_MS) {
      return Promise.resolve(cached.data);
    }
    if (cached) suggestionCache.delete(key);

    suggestRequested = true;
    // Typed queries only: getEmptyState and warmSuggestPath use the proxy without a hedge.
    var proxyUrl = predictiveProxyUrl(q);
    var legs = directUsable() ? [directSuggestUrl(q), proxyUrl] : [proxyUrl, proxyUrl];
    return fetchSuggestHedged(legs, signal, key ? HEDGE_MS : 0, diag).then(function (data) {
      captureDirect(data);
      suggestionCache.delete(key);
      suggestionCache.set(key, { data: data, ts: Date.now() });
      while (suggestionCache.size > SUGGEST_CACHE_MAX) {
        suggestionCache.delete(suggestionCache.keys().next().value);
      }
      return data;
    });
  }

  var EMPTY_STATE_FALLBACK = {
    brands: [
      { value: "Square D", url: "/collections/schneider-electric-square-d-circuit-breakers" },
      { value: "Siemens", url: "/collections/siemens-circuit-breakers" },
      { value: "Eaton", url: "/collections/eaton-cutler-hammer-circuit-breakers" },
      { value: "Cutler-Hammer", url: "/collections/eaton-cutler-hammer-circuit-breakers" },
      { value: "ABB", url: "/collections/general-electric-ge-circuit-breakers" }
    ]
  };
  function normalizeEmptyStateBrands(brands) {
    if (!Array.isArray(brands)) return [];
    return brands.slice(0, 4).filter(function (brand) {
      return brand && typeof brand.value === "string" && brand.value.trim() &&
        typeof brand.url === "string" && /^\/(?!\/)/.test(brand.url) &&
        !/[\\\u0000-\u001f\u007f]/.test(brand.url);
    }).map(function (brand) {
      return { value: brand.value, url: brand.url };
    });
  }
  var emptyStateCache = (function () {
    try {
      var stored = JSON.parse(sessionStorage.getItem("sfs_empty_v1") || "null");
      if (!stored || !Array.isArray(stored.brands)) return null;
      var brands = normalizeEmptyStateBrands(stored.brands);
      if (brands.length !== Math.min(stored.brands.length, 4)) return null;
      var cached = { brands: brands };
      if (stored.hints !== undefined) {
        if (!Array.isArray(stored.hints) || !stored.hints.every(function (hint) { return typeof hint === "string"; })) return null;
        cached.hints = stored.hints;
      }
      // Replace older, larger payloads so the next boot reads only these fields.
      try { sessionStorage.setItem("sfs_empty_v1", JSON.stringify(cached)); } catch (e) {}
      return cached;
    } catch (e) {}
    return null;
  })();
  var emptyStatePromise = null;
  function getEmptyState() {
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled() && window.SFSNativeFallback.inCooldown()) return Promise.resolve(emptyStateCache || EMPTY_STATE_FALLBACK);
    if (!emptyStatePromise) {
      suggestRequested = true;
      emptyStatePromise = fetchJSON(predictiveProxyUrl("")).then(function (data) {
        captureDirect(data);
        var normalized = { brands: [] };
        if (data && Array.isArray(data.brands)) normalized.brands = normalizeEmptyStateBrands(data.brands);
        if (data && Array.isArray(data.hints) && data.hints.every(function (hint) { return typeof hint === "string"; })) {
          normalized.hints = data.hints.slice();
        }
        emptyStateCache = normalized;
        try {
          sessionStorage.setItem("sfs_empty_v1", JSON.stringify(normalized));
        } catch (e) {}
        return normalized;
      }).catch(function () {
        emptyStatePromise = null;
        return null;
      });
    }
    return emptyStatePromise;
  }

  // Idle warm-up. Measured 2026-09-04: a shopper who focuses the box and pauses
  // fires the empty-query suggest, which warms the app-proxy path, and the
  // first typed query then renders about 4 ms after the last key. A shopper who
  // types the instant the page loads pays about 1.1 s for that cold path
  // instead. This sends an empty-query request at idle, retries one failure,
  // and stores its direct token. It never touches dropdown state, debounce timers or
  // committed-search logic.
  var suggestPathWarmed = false;
  function warmSuggestPath() {
    if (suggestPathWarmed) return;
    suggestPathWarmed = true;
    var attempts = 0;
    function attemptWarmSuggest() {
      if (attempts >= 2 || suggestRequested) return; // the shopper already warmed it
      var connection = navigator.connection;
      if (connection && connection.saveData) return;
      var active = document.activeElement;
      if (active && typeof active.matches === "function" && active.matches("input.js-search-input")) return;
      if (!document.querySelector("input.js-search-input")) return;
      attempts++;
      // `priority` is ignored by browsers without fetch priority support.
      var init = { headers: { Accept: "application/json" }, priority: "low" };
      // Nine seconds clears a seven-second cold proxy stall on either attempt.
      fetchWithTimeout(predictiveProxyUrl(""), init, 9000).then(function (res) {
        if (!res.ok) throw new Error("suggest warm-up response failed");
        return res.json();
      }).then(captureDirect)["catch"](function () {
        if (attempts < 2 && !suggestRequested) {
          dlog("suggest warm-up retry");
          setTimeout(attemptWarmSuggest, 1000);
          return;
        }
        dlog("suggest warm-up skipped");
      });
    }
    attemptWarmSuggest();
  }

  var speculationSupported = !!(window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports("speculationrules"));
  var SPECULATION_DEBOUNCE_MS = 120; // extra settle time past render() itself so mid-typing queries never get their own prerender
  var speculationTimer = null;
  var speculationTag = null; // the one <script type="speculationrules"> this file ever owns

  // Match the browser's GET form field order and encoding.
  function commitResultsUrl(form, q) {
    var params = new URLSearchParams();
    var qAppended = false;
    if (form) {
      Array.prototype.forEach.call(form.elements, function (el) {
        if (!el.name || el.disabled) return;
        if ((el.type === "checkbox" || el.type === "radio") && !el.checked) return;
        if (el.name === "q") {
          params.append(el.name, q);
          qAppended = true;
          return;
        }
        params.append(el.name, el.value);
      });
    }
    if (!qAppended) params.append("q", q);
    var action = (form && form.getAttribute("action")) || PREDICTIVE_PROXY_PATH;
    var destination = action + "?" + params.toString();
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) {
      return window.SFSNativeFallback.presentHref(destination);
    }
    return destination;
  }

  // Called only from render()'s success path (a settled query that actually
  // painted product rows) -- never the zero/fallback paths, which are not
  // where a shopper is about to commit. Debounced so a run of successful
  // renders during fast typing replaces the pending rule instead of
  // stacking script tags; invalidatePendingWork()/hide() never cancel this
  // timer or remove an already-injected tag -- the last settled query stays
  // the likely commit even after the panel closes.
  //
  // Runs for every settled render, including a pinned exact-SKU match
  // (2026-09-10 fix): a bare Enter press never routes to the product page --
  // the custom SKU layer that used to patch the commit href at runtime
  // (highlight_product / highlight_handle / family) was removed from this
  // theme (see sections/predictive-search.liquid's own note on that
  // removal), so today every commit, pinned or not, submits the same form to
  // the same /a/search URL commitResultsUrl computes below. Only a direct
  // mouse click on the pinned dropdown row (a plain <a href="/products/...">
  // in renderDropdownProductRow) ever lands on the product page, and that
  // path never goes through this function.
  function scheduleResultsPrerender(form, q, delayMs) {
    if (!speculationSupported) return;
    clearTimeout(speculationTimer);
    speculationTimer = setTimeout(function () {
      speculationTimer = null;
      var url = commitResultsUrl(form, q);
      if (speculationTag) speculationTag.remove();
      speculationTag = document.createElement("script");
      speculationTag.type = "speculationrules";
      speculationTag.textContent = JSON.stringify({ prerender: [{ urls: [url] }] });
      document.head.appendChild(speculationTag);
    }, typeof delayMs === "number" ? delayMs : SPECULATION_DEBOUNCE_MS);
  }

  function SfsDropdown(input) {
    this.input = input;
    this.form = input.closest("form");
    this.resultBox = this.form && this.form.querySelector(".js-search-result");
    this.nativeControlsId = input.getAttribute("aria-controls");
    this.panelId = this.nativeControlsId || (this.resultBox && this.resultBox.id) || "predictive-search-results";
    this.panel = this.resultBox;
    this.reqToken = 0;
    this.zeroTimer = null;
    this.stallTimer = null;
    this.requestController = null;
    this.activeOptionIndex = -1;
    this.pendingOptionDirection = 0;
    this.committed = false;
    this.dismissed = false;
    this.closeButton = this.form && this.form.querySelector(".search__clear");
    this.input.sfsDropdown = this;
    this.viewportFrame = 0;
    if (!this.resultBox) return;
    this.panel.classList.add("sfs-overlay");
    this.panel.setAttribute("aria-hidden", "true");
    this.onViewportChange = this.syncPanelViewport.bind(this);
    this.onViewportScroll = this.queuePanelViewportSync.bind(this);
    window.addEventListener("resize", this.onViewportChange, { passive: true });
    window.addEventListener("scroll", this.onViewportScroll, { passive: true });
    if (window.visualViewport && window.visualViewport.addEventListener) {
      window.visualViewport.addEventListener("resize", this.onViewportChange, { passive: true });
      window.visualViewport.addEventListener("scroll", this.onViewportChange, { passive: true });
    }
    this.bind();
  }

  // The dropdown's own rules (.search-result-container contents,
  // .predictive__*, .sfs-*) live in assets/search-dropdown.css, split out of
  // header.css so pages that never touch the search box never download
  // them. header.css keeps only the resting-state `display: none`, so the
  // link goes in on the first hover/pointerdown/focus of the form -- a few
  // frames before openNow() paints anything into the panel -- and showPanel
  // guarantees it for any other path. Every DOM touch is guarded: the
  // contract-test sandboxes run this file against a stub document.
  var dropdownStylesRequested = false;
  function dropdownStylesHref() {
    var configured = typeof window !== "undefined" && window.theme && window.theme.assets && window.theme.assets.searchDropdownStyles;
    if (configured) return configured;
    var headerLink = document.querySelector('link[rel="stylesheet"][href*="header.css"]');
    if (headerLink && headerLink.href) return headerLink.href.replace(/header\.css/, "search-dropdown.css");
    return null;
  }
  function ensureDropdownStyles() {
    if (dropdownStylesRequested) return;
    dropdownStylesRequested = true;
    try {
      if (document.querySelector("link[data-sfs-dropdown-css]")) return;
      var href = dropdownStylesHref();
      if (!href) return;
      var link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.setAttribute("data-sfs-dropdown-css", "");
      document.head.appendChild(link);
    } catch (e) {
      // A missing link only costs a late style pass; never block the panel.
    }
  }

  SfsDropdown.prototype.bind = function () {
    var self = this;
    this.input.addEventListener("input", function () {
      self.committed = false;
      self.dismissed = false;
      window.__sfsSearchCommitted = false;
      self.invalidatePendingWork();
      self.clearActiveOption();
    });
    this.input.addEventListener(
      "input",
      debounce(function () {
        self.onInput();
      }, function () {
        return isSkuLikeQuery(self.input.value) ? SKU_DEBOUNCE_MS : DEBOUNCE_MS;
      })
    );
    // Item 6: click always opens now. Focus opens now only when empty
    // (unchanged) or pointer-driven, so a keyboard Tab into a prefilled
    // box stays quiet.
    // Warm the dropdown stylesheet on the earliest intent signal (hover,
    // press, or keyboard focus landing anywhere in the form) so it is
    // usually parsed before the first paint into the panel.
    if (this.form && typeof this.form.addEventListener === "function") {
      ["pointerenter", "pointerdown", "focusin"].forEach(function (evt) {
        self.form.addEventListener(evt, ensureDropdownStyles, { once: true, passive: true });
      });
    }
    this.input.addEventListener("pointerdown", function () {
      self.pointerFocus = true;
    });
    this.input.addEventListener("blur", function () {
      self.pointerFocus = false;
    });
    this.input.addEventListener("focus", function () {
      var pointerDriven = self.pointerFocus;
      self.pointerFocus = false;
      if (!self.input.value.trim() || pointerDriven) self.openNow();
    });
    this.input.addEventListener("click", function () {
      self.openNow();
    });
    this.input.addEventListener("keydown", function (e) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        self.moveActiveOption(e.key === "ArrowDown" ? 1 : -1);
        return;
      }
      if ((e.key === "Home" || e.key === "End") && self.isPanelOpen()) {
        e.preventDefault();
        self.setActiveOption(e.key === "Home" ? 0 : self.getOptionElements().length - 1);
        return;
      }
      if (e.key === "Enter") {
        if (self.activateActiveOption()) {
          e.preventDefault();
          return;
        }
        self.commit();
        sendBeacon(self.input.value.trim(), "enter", null);
      }
      if (e.key === "Escape" && self.isPanelOpen()) {
        e.preventDefault();
        self.invalidatePendingWork();
        self.hide();
      }
    });
    if (this.form) {
      this.form.addEventListener(
        "submit",
        function () {
          self.commit();
        },
        true
      );
    }
    var container = this.input.closest(".form-search-container") || this.form;
    container.addEventListener("focusout", function () {
      setTimeout(function () {
        if (!container.contains(document.activeElement)) {
          dlog("overlay closed (focusout parity)");
          self.invalidatePendingWork();
          self.hide();
        }
      });
    });
  };

  // Reuse the one Liquid visual and ARIA panel.
  SfsDropdown.prototype.ensurePanel = function () {
    return this.panel;
  };

  SfsDropdown.prototype.isPanelOpen = function () {
    return !!(this.panel && this.panel.classList.contains("show"));
  };

  SfsDropdown.prototype.clearPanelViewport = function () {
    if (!this.panel) return;
    this.panel.style.removeProperty("--sfs-available-height");
    this.panel.style.removeProperty("--sfs-panel-left");
    this.panel.style.removeProperty("--sfs-panel-width");
  };

  SfsDropdown.prototype.queuePanelViewportSync = function () {
    if (this.viewportFrame) return;
    var self = this;
    this.viewportFrame = window.requestAnimationFrame(function () {
      self.viewportFrame = window.requestAnimationFrame(function () {
        self.viewportFrame = 0;
        self.syncPanelViewport();
      });
    });
  };

  SfsDropdown.prototype.syncPanelViewport = function () {
    if (!this.panel || !this.isPanelOpen()) return;
    if (this.closeButton) this.closeButton.hidden = !this.input.value && !isMobileSearchViewport();

    var viewport = window.visualViewport;
    var viewportHeight = viewport && viewport.height ? viewport.height : window.innerHeight;
    var viewportWidth = viewport && viewport.width
      ? viewport.width
      : document.documentElement.clientWidth || window.innerWidth;
    var viewportTop = viewport && viewport.offsetTop ? viewport.offsetTop : 0;
    var viewportLeft = viewport && viewport.offsetLeft ? viewport.offsetLeft : 0;
    var panelTop = this.panel.getBoundingClientRect().top;
    var availableHeight = Math.floor(viewportTop + viewportHeight - panelTop - 12);
    this.panel.style.setProperty("--sfs-available-height", Math.max(48, availableHeight) + "px");

    var body = document.body;
    var header = this.input.closest("header");
    var searchMode = !!(body && body.matches(".sb-search-mode"));
    var isCompact = !!(body && body.classList.contains("sb-header-compact"));
    var isSticky = !!(header && header.classList.contains("header-sticky"));
    var isShortLandscape = viewportWidth >= 750 && viewportWidth < 992 && viewportHeight <= 500;
    var shouldMatchViewport = !!this.input.closest(".header-search")
      && !searchMode
      && viewportWidth < 992
      && (isCompact || isSticky || isShortLandscape);

    if (!shouldMatchViewport) {
      this.panel.style.removeProperty("--sfs-panel-left");
      this.panel.style.removeProperty("--sfs-panel-width");
      return;
    }

    var inset = viewportWidth < 750 ? 12 : viewportWidth * 0.05;
    var offsetParent = this.panel.offsetParent;
    var parentLeft = offsetParent ? offsetParent.getBoundingClientRect().left : 0;
    this.panel.style.setProperty("--sfs-panel-left", Math.round(viewportLeft + inset - parentLeft) + "px");
    this.panel.style.setProperty("--sfs-panel-width", Math.max(0, Math.floor(viewportWidth - inset * 2)) + "px");
  };

  SfsDropdown.prototype.getOptionElements = function () {
    if (!this.panel) return [];
    return Array.prototype.slice.call(this.panel.querySelectorAll('[role="option"]')).filter(function (option) {
      return !option.hidden && option.getAttribute("aria-hidden") !== "true" && option.getClientRects().length > 0;
    });
  };

  SfsDropdown.prototype.prepareOptions = function () {
    if (!this.panel) return;
    var optionIdBase = this.panel.id + "-option-";
    this.panel.querySelectorAll('[role="option"]').forEach(function (option, index) {
      option.id = optionIdBase + index;
      option.setAttribute("aria-selected", "false");
      option.style.removeProperty("background-color");
      option.style.removeProperty("outline");
      option.style.removeProperty("outline-offset");
    });
    this.activeOptionIndex = -1;
    this.input.removeAttribute("aria-activedescendant");
  };

  SfsDropdown.prototype.clearActiveOption = function () {
    if (this.panel) {
      this.panel.querySelectorAll('[role="option"]').forEach(function (option) {
        option.setAttribute("aria-selected", "false");
        option.style.removeProperty("background-color");
        option.style.removeProperty("outline");
        option.style.removeProperty("outline-offset");
      });
    }
    this.activeOptionIndex = -1;
    this.input.removeAttribute("aria-activedescendant");
  };

  SfsDropdown.prototype.setActiveOption = function (index) {
    var options = this.getOptionElements();
    if (!options.length || index < 0) return false;
    var nextIndex = Math.min(index, options.length - 1);
    this.clearActiveOption();
    var option = options[nextIndex];
    this.activeOptionIndex = nextIndex;
    option.setAttribute("aria-selected", "true");
    this.input.setAttribute("aria-activedescendant", option.id);
    option.style.backgroundColor = "var(--pd-wash)";
    option.style.outline = "2px solid var(--pd-action)";
    option.style.outlineOffset = "-2px";
    if (typeof option.scrollIntoView === "function") option.scrollIntoView({ block: "nearest" });
    return true;
  };

  SfsDropdown.prototype.moveActiveOption = function (direction) {
    var options = this.getOptionElements();
    if (!this.isPanelOpen() || !options.length) {
      this.pendingOptionDirection = direction;
      this.openNow();
      return false;
    }
    var nextIndex = this.activeOptionIndex;
    if (nextIndex < 0) nextIndex = direction > 0 ? 0 : options.length - 1;
    else nextIndex = (nextIndex + direction + options.length) % options.length;
    return this.setActiveOption(nextIndex);
  };

  SfsDropdown.prototype.activateActiveOption = function () {
    var options = this.getOptionElements();
    var option = options[this.activeOptionIndex];
    if (!option) return false;
    var target = option.matches("a[href], button") ? option : option.querySelector("a[href], button");
    if (!target) return false;
    // Clear uses Tab, so option navigation no longer reaches this guard for it.
    var staysOpen = target.classList.contains("sfs-recent-clear");
    if (!staysOpen) {
      this.committed = true;
      window.__sfsSearchCommitted = true;
      this.invalidatePendingWork();
    }
    target.click();
    if (!staysOpen) this.hide();
    return true;
  };

  SfsDropdown.prototype.showPanel = function () {
    ensureDropdownStyles();
    if (this.dismissed) return;
    var panel = this.ensurePanel();
    this.clearPendingState();
    panel.classList.add("show");
    panel.setAttribute("aria-hidden", "false");
    this.input.setAttribute("aria-controls", panel.id);
    this.input.setAttribute("aria-expanded", "true");
    if (this.closeButton) this.closeButton.hidden = !this.input.value && !isMobileSearchViewport();
    this.syncPanelViewport();
    this.prepareOptions();
    if (this.pendingOptionDirection) {
      var direction = this.pendingOptionDirection;
      this.pendingOptionDirection = 0;
      this.moveActiveOption(direction);
    }
  };

  // Recent-row commit path (spec: "fills the input and triggers the
  // standard flow, so analytics and the interceptor see it" -- deliberately
  // different from Popular/Brands, which stay plain hrefs, "same as
  // today"). Sets the input value via a real 'input' event rather than
  // navigating directly, so this file's own onInput handler AND any other
  // listener on the box -- including the SKU search interceptor -- see it
  // exactly like a shopper typing it, then submits the form the same way
  // Enter would.
  SfsDropdown.prototype.runQuery = function (q) {
    this.input.value = q;
    this.input.dispatchEvent(new Event("input", { bubbles: true }));
    this.input.focus();
    if (this.form && typeof this.form.requestSubmit === "function") this.form.requestSubmit();
    else if (this.form) this.form.submit();
  };

  // Item 6: paints Recent + a Searching placeholder before the /suggest
  // round trip so a click into a prefilled box shows feedback at once.
  SfsDropdown.prototype.paintInterimPanel = function () {
    if (this.committed) return;
    if (window.__sfsSearchCommitted) return;
    var self = this;
    var panel = this.ensurePanel();
    var recent = getRecentSearches();
    var recentBlock = recent.length
      ? '<div class="predictive__search-result sfs-empty-state"><div class="sfs-empty-groups">' +
        recentSearchesGroupHtml(recent) +
        "</div></div>"
      : "";
    panel.innerHTML = recentBlock + '<p class="sfs-searching-status" role="status">Searching...</p>';
    wireRecentRows(panel, self, function () {
      self.paintInterimPanel();
    });
    this.showPanel();
  };

  // A click/focus requests now; typing remains debounced.
  SfsDropdown.prototype.openNow = function () {
    if (this.committed) return;
    if (window.__sfsSearchCommitted) return;
    this.dismissed = false;
    var value = this.input.value.trim();
    if (!value) {
      this.onEmptyFocus();
      return;
    }
    if (value.length >= MIN_CHARS && !this.isPanelOpen()) this.paintInterimPanel();
    this.onInput();
  };

  // A commit cancels late work without blocking the native form submit.
  SfsDropdown.prototype.commit = function () {
    this.committed = true;
    window.__sfsSearchCommitted = true;
    this.invalidatePendingWork();
    this.hide();
  };

  SfsDropdown.prototype.invalidatePendingWork = function () {
    ++this.reqToken;
    this.clearZeroTimer();
    // Every abort path clears its timer and pending paint here.
    this.clearStallTimer();
    this.clearPendingState();
    if (this.requestController) {
      this.requestController.abort();
      this.requestController = null;
    }
  };

  // QC finding (2026-08-26, Playwright repro on preview 189038985529): a
  // stall on the FIRST /suggest response for an input painted nothing at
  // all -- no aria-busy, no "Searching..." text -- because this used to
  // bail out whenever isPanelOpen() was false. That guard was fine as long
  // as SOMETHING had already opened the panel first (a prior response, or
  // onEmptyFocus's own empty-state paint), but when the very first request
  // on a fresh input stalls past STALL_MS, nothing has opened the panel
  // yet, so the early return left the shopper with no feedback at all even
  // though the request stayed current and results eventually rendered.
  // Fixed by opening the panel here too, via the same showPanel() every
  // other paint path already uses, when it isn't open yet -- called before
  // the min-height freeze below so showPanel()'s own clearPendingState()
  // can't wipe out a min-height this function is about to set.
  SfsDropdown.prototype.showPendingState = function () {
    if (this.input.value.trim().length < MIN_CHARS) return;
    var panel = this.ensurePanel();
    if (!this.isPanelOpen()) this.showPanel();
    if (panel.offsetHeight) panel.style.minHeight = panel.offsetHeight + "px";
    panel.setAttribute("aria-busy", "true");
    // Item 6: don't wipe the interim panel's Recent rows / option ids.
    if (panel.querySelector(".sfs-searching-status")) return;
    panel.innerHTML = '<p class="sfs-searching-status" role="status">Searching...</p>';
  };

  SfsDropdown.prototype.clearPendingState = function () {
    if (!this.panel) return;
    this.panel.style.removeProperty("min-height");
    this.panel.removeAttribute("aria-busy");
  };

  // Arms the stall threshold for one in-flight /suggest request (token
  // identifies it). Fires only if that exact request is still current
  // STALL_MS after it started -- a response, a supersede, or any abort path
  // clears this first (see clearStallTimer callers), so a fast reply never
  // reaches this callback at all and aria-busy never mirrors a request that
  // already finished.
  SfsDropdown.prototype.armStallTimer = function (token) {
    var self = this;
    this.stallTimer = setTimeout(function () {
      self.stallTimer = null;
      if (token !== self.reqToken) return;
      self.showPendingState();
    }, STALL_MS);
  };

  SfsDropdown.prototype.clearStallTimer = function () {
    if (this.stallTimer) {
      clearTimeout(this.stallTimer);
      this.stallTimer = null;
    }
  };

  SfsDropdown.prototype.clearZeroTimer = function () {
    if (this.zeroTimer) {
      clearTimeout(this.zeroTimer);
      this.zeroTimer = null;
      dlog("zero paint cancelled");
    }
  };

  SfsDropdown.prototype.dismiss = function () {
    this.dismissed = true;
    this.invalidatePendingWork();
    this.input.blur();
    if (this.closeButton && document.activeElement === this.closeButton) {
      var submitButton = this.form.querySelector('[type="submit"]');
      if (submitButton) submitButton.focus({ preventScroll: true });
    }
    this.hide();
  };

  SfsDropdown.prototype.hide = function () {
    this.clearZeroTimer();
    this.clearPendingState();
    this.pendingOptionDirection = 0;
    this.clearActiveOption();
    this.input.setAttribute("aria-expanded", "false");
    if (this.closeButton) this.closeButton.hidden = !this.input.value;
    if (this.nativeControlsId) this.input.setAttribute("aria-controls", this.nativeControlsId);
    else this.input.removeAttribute("aria-controls");
    this.input.removeAttribute("aria-activedescendant");
    if (this.panel) {
      this.panel.innerHTML = "";
      this.panel.classList.remove("show");
      this.panel.setAttribute("aria-hidden", "true");
      this.clearPanelViewport();
    }
  };

  SfsDropdown.prototype.onEmptyFocus = function () {
    this.invalidatePendingWork();
    var initialData = emptyStateCache || EMPTY_STATE_FALLBACK;
    this.renderEmptyState(initialData);
    dlog("empty state instant", emptyStateCache ? "cache" : "fallback");
    // Warm the search path and capture its direct token without repainting.
    getEmptyState();
  };

  // No native equivalent (native predictive search does nothing on an empty
  // query) -- the only surface in this file that isn't a reuse of existing
  // theme markup, per Sagar's point 5. Still built from the same
  // predictive__search-group / li>a>p primitives for visual consistency.
  //
  // Section order top to bottom (spec 2026-08-21-search-empty-state-polish-
  // design.md, Direction A locked with Sagar): Recent (only when >=1
  // exists, first thing under the input) -> Popular Brands
  // -> Popular Categories (five fixed rows with Shop All in the heading)
  // -> tip line. top-of-panel multi-line hints block.
  //
  // 2026-08-22: Categories (the standalone `categories` array) removed
  // entirely. The engine's emptyState.js returned `categories: []` at the
  // time (documented at the top of that file as a fixed duplicate-list bug
  // -- Categories used to just re-list the same product types Popular
  // showed back then). group() below stays defensive on an empty array
  // regardless (renders nothing), so this needed no guard change, only
  // removing the group("Categories", ...) call and retitling the other
  // two: "Popular" -> "Popular Amperages" and "Brands" -> "Popular Brands".
  //
  // 2026-08-23: the `/suggest` payload changed again -- `categories` is now
  // populated (12 items) but still feeds only the zero-results page, never
  // this dropdown, so it stays ignored here. `popular` is no longer
  // amperage-only; it is a revenue-ranked MIXED list (amperage, poles,
  // mounting style, top non-breaker product types), so the heading is
  // corrected here from "Popular Amperages" to "Popular Categories" to
  // match what actually renders. Brands use fixed collection destinations.
  SfsDropdown.prototype.renderEmptyState = function (data) {
    if (this.committed) return; // a commit landed while /suggest?q= (getEmptyState) was in flight
    var panel = this.ensurePanel();
    var self = this;
    var recent = getRecentSearches();
    var popularCurated = DROPDOWN_CATS;
    // Keep the same five brand names and parent collections as the menu.
    var brandsTop = EMPTY_STATE_FALLBACK.brands;

    // Reuses .predictive__search-group / h3 / ul / li / a / p (the same
    // primitives the typed-query rail uses) so the group looks like part of
    // the same dropdown -- but NOT .predictive-search-secondary, which
    // header.css hides outright at <750px (that rule exists to hand mobile
    // over to the product list, which doesn't apply here). Density and
    // 3-up/stacked layout are owned by the sfs-empty-group/-groups rules in
    // header.css. Phase 2 app-proxy cutover (2026-08-21 spec): these
    // Popular/Brands rows are dropdown commits too (a direct click
    // navigates straight to the results page, same as "View all"), so they
    // get the same /a/search destination -- params unchanged, only the
    // leading path segment moved.
    //
    // Capped at 6 (2026-08-23: raised from 5 to match curatePopular's own
    // 6-row cap above, now that both `popular` and `brands` arrive as
    // 6-item lists -- see the 2026-08-23 note above curatePopular). Same
    // cut-off pattern Recent already uses via RECENT_MAX below. Both engine
    // sections (popular, brands) already arrive revenue-ranked and
    // pre-capped server-side, so this slice is belt-and-suspenders, not the
    // primary limiter.
    //
    // labelFn: Popular used to pass titleCasePopularLabel here to re-case
    // raw shopper QUERIES ("qob" -> "QOB"), then briefly held catalog
    // product_type values -- both sources are gone now. The engine's
    // `popular`/`brands` values (whether an amperage, a brand group, or any
    // other category label) need no re-casing, so labelFn still has no
    // caller for this column. titleCasePopularLabel stays deleted; every
    // group() row renders the engine's value verbatim. Rows carry no
    // `(count)` span (see the item.count guard just below) because the new
    // payload no longer sends counts at all -- the engine omits the key
    // entirely rather than sending a fake 0. labelFn stays as an optional
    // 3rd parameter -- no caller uses it today, kept only as a documented,
    // harmless hook rather than ripped out, since a future group could
    // still need a display-only transform without touching the href.
    // sfs_browse=1 (2026-08-22, Sagar's call): only group()'s two curated
    // callers -- Popular Categories and Popular Brands -- carry this. It
    // tells the results page to render a category heading ("20 Amp") instead
    // of a search heading ("Results for ..."), since clicking a curated row
    // is browsing, not a typed query. viewAllButtonHtml's href above (the
    // "View all"/"Search for ..." commit, and the header/mobile form's own
    // action) never gets this marker -- those are real typed searches.
    // Recent rows don't go through group() at all -- runQuery() fills the
    // input and submits the form, so they can't carry it either. Ordering
    // and encoding of the existing params is unchanged; app-proxy HMAC
    // verification is order-independent (Shopify signs whatever arrives).
    //
    // 2026-08-23: the engine now sends a real collection path on `item.url`
    // for most rows (e.g. "/collections/100-amp-circuit-breakers"). When
    // present, the row links straight there instead of building the
    // /a/search query href -- a direct category-page visit, not a text
    // search. When item.url is absent, the row falls back to today's exact
    // /a/search?q=... behaviour unchanged. Either way this stays a plain
    // <a href> inside <li role="option">, so keyboard reachability
    // (Tab/Enter) and screen-reader semantics are identical for both cases.
    var group = function (title, arr, labelFn, moreHref) {
      if (!arr || !arr.length) return "";
      var more = moreHref
        ? '<a href="' + esc(moreHref) + '" class="sfs-empty-more" tabindex="-1" role="option">Shop All &gt;</a>'
        : "";
      var items = arr
        .slice(0, 6)
        .map(function (item) {
          var label = labelFn ? labelFn(item.value) : item.value;
          var href = item.url
            ? esc(item.url)
            : "/a/search?q=" +
              encodeURIComponent(item.value) +
              "&type=product&options%5Bunavailable_products%5D=last&options%5Bprefix%5D=last&sfs_cards=liquid&sfs_browse=1";
          if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) href = esc(window.SFSNativeFallback.presentHref(href));
          return (
            '<li role="option"><a href="' +
            href +
            '" tabindex="-1"><p class="">' +
            esc(label) +
            (item.more ? ' <span aria-hidden="true">&rarr;</span>' : "") +
            (item.count ? ' <span class="sfs-count">(' + item.count + ")</span>" : "") +
            "</p></a></li>"
          );
        })
        .join("");
      return '<div class="predictive__search-group sfs-empty-group"><h3><span class="sfs-empty-title">' + esc(title) + "</span>" + more + '</h3><ul role="group">' + items + "</ul></div>";
    };

    // Recent rows are NOT plain hrefs like the groups above -- see
    // SfsDropdown.prototype.runQuery's own comment for why. Wired up below,
    // once the panel is actually in the DOM. sfs-recent-group carries its
    // own block above the Brands/Categories pair (.sfs-empty-cols), not
    // a third grid column. Renders nothing at all (no heading, no empty-state copy) when
    // recent.length is 0 -- recentHtml just stays "" and contributes nothing
    // to the innerHTML concat below (Sagar's point 5, verified already true
    // here, not a new change).
    var recentHtml = recent.length ? recentSearchesGroupHtml(recent) : "";

    // .predictive__search-result on the outer wrapper picks up header.css's
    // container rules (bounded max-height + scroll on desktop, native
    // scrollbar styling) without picking up its 2-col rail/product grid --
    // that grid only turns on when a .predictive__search-rail child is
    // present (see header.css ~line 1389), which this panel never has.
    // Tip built once (Sagar 2026-08-24): no Recent -> tip renders above the
    // groups wrap; one or more Recent -> tip stays below, today's place.
    // Clearing Recent re-renders via this same function, so the tip jumps
    // to the top the instant the list goes empty.
    var tipHtml = tipTeachHtml(!recent.length);
    panel.innerHTML =
      '<div class="predictive__search-result sfs-empty-state">' +
      (recent.length ? "" : tipHtml) +
      '<div class="sfs-empty-groups">' +
      recentHtml +
      '<div class="sfs-empty-cols">' +
      group("Popular Brands", brandsTop, null, "/pages/brands") +
      group("Popular Categories", popularCurated, null, "/collections/all") +
      "</div>" +
      "</div>" +
      (recent.length ? tipHtml : "") +
      "</div>";

    wireRecentRows(panel, self, function () {
      self.renderEmptyState(data);
    });
    panel.querySelectorAll(".sfs-tip-chip").forEach(function (a) {
      a.addEventListener("click", function (e) {
        e.preventDefault();
        self.runQuery(a.getAttribute("data-q"));
      });
    });
    this.showPanel();
  };

  SfsDropdown.prototype.renderFetchError = function () {
    if (this.committed) return; // a commit landed before this fired -- nothing left to warn about
    var panel = this.ensurePanel();
    panel.innerHTML = '<p class="sfs-fetch-error">Results are not loading right now. Press Enter to run the full search.</p>';
    this.showPanel();
  };

  SfsDropdown.prototype.onInput = function () {
    if (this.dismissed) return;
    var self = this;
    this.invalidatePendingWork();
    var q = this.input.value.trim();
    var token = this.reqToken;
    if (q.length < MIN_CHARS) {
      this.hide();
      return;
    }
    var controller = new AbortController();
    var signal = controller.signal;
    this.requestController = controller;
    // Stall threshold, not an eager paint: armStallTimer only shows
    // "Searching..." if THIS request is still current STALL_MS from now.
    // Armed exactly once per keystroke, here -- the retry/fallback chain
    // below never re-arms it, so whatever it paints (or is still waiting
    // to paint) rides across the whole chain untouched until settle.
    this.armStallTimer(token);
    // One diagnostic record per keystroke cycle -- see the "per-query
    // suggest diagnostics" block above (near suggestRequested) for what
    // this measures and where it goes.
    var diag = { q_len: q.length, legs: [], startedAt: Date.now(), finalized: false };
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) {
      this.runPreviewSuggest(q, token, signal);
    } else this.runSuggest(q, token, signal, false, undefined, undefined, diag);
  };

  SfsDropdown.prototype.runPreviewSuggest = function (q, token, signal) {
    var self = this;
    window.SFSNativeFallback.fetchSuggest(q, signal).then(function (outcome) {
      if (!self.isRequestCurrent(token, q, signal) || outcome.stale) return;
      if (outcome.data) {
        self.clearStallTimer();
        self.render(q, outcome.data, token, signal);
      } else if (outcome.fallback) {
        self.runNativeFallback(q, token, signal);
      }
    });
  };

  // True only while this exact request (token/q) still owns the panel and
  // its owner signal hasn't been aborted by a newer keystroke, Escape, or a
  // commit -- the one staleness check every leg of the retry/fallback chain
  // below re-runs before it is allowed to touch the panel.
  SfsDropdown.prototype.isRequestCurrent = function (token, q, signal) {
    return !signal.aborted && token === this.reqToken && this.input.value.trim() === q;
  };

  // Resilience chain for the dropdown /suggest fetch. Two shapes, both at
  // most one engine retry per typed word:
  // - Non-overload failure (network error, 500, etc): original attempt ->
  //   exactly one immediate retry (its own fresh TIMEOUT_MS budget, same
  //   owner `signal` so a supersede's invalidatePendingWork()->abort() still
  //   kills it instantly) -> native predictive-search fallback ->
  //   renderFetchError() if that also fails.
  // - Overload failure (429/503, round 2 fix 2026-09-08): native fallback
  //   runs IMMEDIATELY on the first failure so the shopper is never staring
  //   at a stalled panel, while the single retry still runs after the
  //   backoff in parallel, racing the SAME owner signal. Ownership rule
  //   (round 3 fix, 2026-09-08): a successful engine retry always wins -- it
  //   flips a shared `nativeRaceState.engineWon` flag before painting, so a
  //   native settle (paint or error) that lands AFTER the engine already
  //   painted -- e.g. Retry-After: 0, or any backoff shorter than native's
  //   own TIMEOUT_MS -- is dropped as a no-op instead of clobbering the
  //   engine's result. A retry that fails instead leaves whatever native
  //   already painted (result or error) exactly as it is.
  // A single failed attempt used to just hide() the panel and leave the
  // shopper with nothing for that keystroke; rare upstream stalls (~7s)
  // hitting the TIMEOUT_MS budget were indistinguishable from a real empty
  // result.
  //
  // Busy-state note: the stall timer armed once in onInput is deliberately
  // left alone across every leg here (no clearStallTimer() on a retryable
  // failure) -- a retry or a fallback fetch IS a genuine stall, not a
  // settle, so the "Searching..." paint it may already own (or may still
  // go on to paint, if STALL_MS hasn't elapsed yet) stays exactly as it
  // is. clearStallTimer() only runs at the points that actually end the
  // chain for a given attempt: a successful render, a successful fallback
  // paint, and the final renderFetchError() when everything failed.
  SfsDropdown.prototype.runSuggest = function (q, token, signal, isRetry, nativeFallbackStarted, nativeRaceState, diag) {
    var self = this;
    getSuggestion(q, signal, diag)
      .then(function (data) {
        self.clearStallTimer();
        if (!self.isRequestCurrent(token, q, signal)) return;
        // render() below is the single gate that decides whether to
        // prerender at all (2026-09-10: it now schedules a prerender for
        // every settled query, pinned exact-SKU match included -- see the
        // comment above the prerender scheduler function for why).
        // Round 3 fix: flip nativeRaceState BEFORE painting so a same-word
        // native settle that lands after this becomes a no-op (see the
        // ownership-rule comment above runSuggest).
        if (nativeRaceState) nativeRaceState.engineWon = true;
        self.render(q, data, token, signal, diag);
        // An engine success is always final per the ownership rule above --
        // safe to finalize even mid overload-race, where a native fallback
        // may already have painted and be about to get overwritten here.
        finalizeSuggestDiag(diag);
      })
      .catch(function (err) {
        // signal.aborted (this request's OWN owner signal, aborted only by
        // invalidatePendingWork) means a newer keystroke, Escape, or a
        // commit already superseded this request -- that newer request (or
        // nothing, if the panel closed) owns the panel now, so this never
        // retries an abort and there is nothing to clean up here.
        if (signal.aborted) return;
        if (!self.isRequestCurrent(token, q, signal)) return;
        if (!isRetry) {
          if (isOverloadStatus(err && err.status)) {
            // W3.3 round 2 (review fix, 2026-09-08): don't make the shopper
            // wait out the backoff before seeing anything. Run the native
            // fallback right now so the panel shows real results at once,
            // and schedule the single engine retry in parallel -- honoring
            // Retry-After when the engine sent one, else waiting 2s plus up
            // to 500ms of jitter. Still exactly one retry per typed word.
            // Round 3 fix: both legs share one nativeRaceState object so the
            // engine winning marks native's own then/catch stale.
            var nativeRaceState = { engineWon: false };
            self.runNativeFallback(q, token, signal, nativeRaceState, diag);
            setTimeout(function () {
              if (signal.aborted) return;
              if (!self.isRequestCurrent(token, q, signal)) return;
              self.runSuggest(q, token, signal, true, true, nativeRaceState, diag);
            }, overloadBackoffMs(err));
            return;
          }
          // Exactly one retry, fresh TIMEOUT_MS budget (getSuggestion's own
          // fetchJSON -> fetchWithTimeout always starts a new internal
          // timer per call); the retry never populates or reads
          // suggestionCache, since that cache is only ever written on
          // success, so this is guaranteed to be a real second network
          // attempt, not a cache replay of the same failure.
          self.runSuggest(q, token, signal, true, false, undefined, diag);
          return;
        }
        // The one retry also failed. The overload branch above already ran
        // runNativeFallback before this retry was even scheduled, so its
        // success/error state already owns the panel -- fetching it a
        // second time here would just race or clobber that paint. Only run
        // it now when this is a non-overload retry, which never ran it yet.
        if (!nativeFallbackStarted) {
          self.runNativeFallback(q, token, signal, undefined, diag);
        } else {
          // Overload race, both sides now failed: native already ran (and
          // skipped finalizing in case this retry won), and the retry just
          // failed too -- native's recorded leg is the final word.
          finalizeSuggestDiag(diag);
        }
      });
  };

  // Reuses the exact request shape the theme's own predictive-search
  // controller builds (see assets/predictive-search.js
  // PredictiveSearch.prototype.getRequest, currently unloaded per
  // search-single-owner-contract but kept as the reference implementation):
  // routes.predictive_search_url (read off the <predictive-search> element
  // this input lives in as data-url, since that Liquid object isn't
  // exposed on window.theme.routes) plus data-section, plus this same
  // form's own hidden type/options[*]/sfs_cards fields, with q overridden
  // to the current query. sections/predictive-search.liquid opens with a
  // literal "<!-- /// -->" marker (line 1) that predictive-search.js's own
  // render splits on and keeps only the half after it -- the exact
  // #resultContainer markup this.panel (.js-search-result) already expects,
  // since that is genuinely the SAME element the native controller used to
  // paint into. Mirrored here rather than reused as a function since that
  // file is a different workstream's exclusive file (AGENTS.md) and is not
  // loaded on the page today.
  //
  // QC finding (2026-08-26): <predictive-search> is never a literal string
  // in any snippet/section -- snippets/form.liquid builds it from a Liquid
  // capture (searchTag = "predictive-search" when settings.enable_predictive_
  // search, else "div"), so a plain grep for the tag finds nothing even
  // though it does render at runtime with this theme's default settings
  // (config/settings_schema.json's enable_predictive_search default is
  // true, and settings_data.json has no override). Belt-and-suspenders
  // against that setting ever being off, a theme edit dropping the
  // attribute, or any other reason the element/data-url isn't there: when
  // the element lookup comes up empty, fall back to Shopify's own
  // documented predictive-search route directly -- "/search/suggest" is
  // the fixed path routes.predictive_search_url always resolves to on
  // every Shopify store, never merchant-configurable -- with the same
  // q/section_id pair, still targeting sections/predictive-search.liquid
  // (still exists on this branch) via the same "predictive-search"
  // section_id. The marker-split failure handling in the caller is
  // unchanged either way: a missing "<!-- /// -->" marker in whatever this
  // returns is still a failure that falls through to renderFetchError().
  SfsDropdown.prototype.nativeFallbackUrl = function (q) {
    var container = this.input.closest("predictive-search");
    var params = new URLSearchParams();
    if (this.form) {
      new FormData(this.form).forEach(function (value, key) {
        if (key === "q") return; // overridden below with the current query
        params.set(key, value);
      });
    }
    params.set("q", q);
    if (container && container.dataset && container.dataset.url) {
      params.set("section_id", container.dataset.section || "predictive-search");
      var nativeHref = container.dataset.url + "?" + params.toString();
      if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) return window.SFSNativeFallback.nativePredictiveUrl(nativeHref);
      return nativeHref;
    }
    params.set("section_id", "predictive-search");
    var defaultHref = "/search/suggest?" + params.toString();
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) return window.SFSNativeFallback.nativePredictiveUrl(defaultHref);
    return defaultHref;
  };

  SfsDropdown.prototype.runNativeFallback = function (q, token, signal, nativeRaceState, diag) {
    var self = this;
    // nativeFallbackUrl always returns a URL now (element-based, or the
    // hardcoded "/search/suggest" route when the element/data-url isn't
    // there) -- this guard is belt-and-suspenders against a future change
    // to that function leaving nothing to fetch, not a live path today.
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) return this.runPreviewNativeFallback(q, token, signal);
    var url = this.nativeFallbackUrl(q);
    if (!url) {
      dlog("native fallback unavailable (no url to fetch)", q);
      self.clearStallTimer();
      if (token === self.reqToken) self.hide();
      return;
    }
    var legStartedAt = Date.now();
    var lastStatus = null;
    fetchWithTimeout(url, { headers: { Accept: "text/html" } }, TIMEOUT_MS, signal)
      .then(function (res) {
        lastStatus = res.status;
        if (!res.ok) throw new Error("http " + res.status);
        return res.text();
      })
      .then(function (html) {
        // Round 3 fix (review, 2026-09-08): a late settle after the engine
        // retry already won (nativeRaceState.engineWon) is a no-op -- never
        // overwrite a strictly-better engine paint with a stale native one.
        if (nativeRaceState && nativeRaceState.engineWon) {
          if (diag) recordSuggestLeg(diag, "native", url, legStartedAt, lastStatus, null);
          return;
        }
        if (!self.isRequestCurrent(token, q, signal)) return;
        var body = (html.split("<!-- /// -->")[1] || "").trim();
        if (!body) throw new Error("empty native predictive-search fragment");
        self.clearStallTimer();
        var panel = self.ensurePanel();
        panel.innerHTML = body;
        self.showPanel();
        dlog("dropdown native fallback painted", q);
        if (diag) {
          recordSuggestLeg(diag, "native", url, legStartedAt, lastStatus, null);
          // Only final here with nothing else able to overwrite this paint.
          // In the overload race (nativeRaceState set) the parallel retry
          // may still win -- runSuggest's own handlers finalize that case.
          if (!nativeRaceState) finalizeSuggestDiag(diag);
        }
      })
      .catch(function (err) {
        // Same abort convention as runSuggest above: a superseded request
        // has nothing left to clean up, the newer one already owns things.
        if (signal.aborted) return;
        // Round 3 fix (review, 2026-09-08): same race as the then-paint
        // above -- an already-won engine paint must never be replaced by a
        // late native failure's error state.
        if (nativeRaceState && nativeRaceState.engineWon) {
          if (diag) {
            var wonClassified = classifySuggestFailure(lastStatus != null ? { status: lastStatus } : err, false, false);
            recordSuggestLeg(diag, "native", url, legStartedAt, wonClassified.status, wonClassified.reason);
          }
          return;
        }
        dlog("native fallback FAIL", q, String(err));
        self.clearStallTimer();
        if (diag) {
          var classified = classifySuggestFailure(lastStatus != null ? { status: lastStatus } : err, false, signal.aborted);
          recordSuggestLeg(diag, "native", url, legStartedAt, classified.status, classified.reason);
        }
        // W3.1: total failure (engine retry AND native fallback both failed)
        // now shows the fetch-error state and keeps the panel open, instead
        // of hide() leaving the shopper with nothing. Enter still submits --
        // this never touches the form/input submit handling.
        if (token === self.reqToken) self.renderFetchError();
        // Same reasoning as the success branch above.
        if (diag && !nativeRaceState) finalizeSuggestDiag(diag);
      });
  };

  SfsDropdown.prototype.runPreviewNativeFallback = function (q, token, signal) {
    var self = this;
    var fallback = window.SFSNativeFallback;
    var url = this.nativeFallbackUrl(q);
    return fallback.fetchBudget(url, window.SFS_NATIVE_FALLBACK.timeoutMs, signal, 'dropdown').then(function (body) {
      if (!self.isRequestCurrent(token, q, signal)) return;
      if (!body || !body.childNodes || !body.childNodes.length) throw new Error('invalid native fragment');
      var nodes = Array.prototype.map.call(body.childNodes, function (node) {
        return document.importNode(node, true);
      });
      self.clearStallTimer();
      self.ensurePanel().replaceChildren.apply(self.ensurePanel(), nodes);
      self.showPanel();
      sendBeacon(q, body.querySelectorAll('.predictive-search-products li').length ? 'dropdown' : 'zero', body.querySelectorAll('.predictive-search-products li').length);
    }).catch(function () {
      if (!self.isRequestCurrent(token, q, signal)) return;
      fallback.record('native_failure', { surface: 'dropdown' });
      self.clearStallTimer();
      self.renderFetchError();
    });
  };

  SfsDropdown.prototype.render = function (q, data, token, signal) {
    if (this.committed || signal.aborted) return; // a commit landed while the /suggest fetch was in flight
    var diag = null;
    try { if (arguments.length > 4) diag = arguments[4]; } catch (eDiag) {}
    var self = this;
    this.clearZeroTimer();
    var progressive = data.progressive && typeof data.progressive === "object" ? data.progressive : null;
    var products = dedupeById(data.pinned, data.products || []).slice(0, progressive ? 10 : 8);

    if (!products.length && data && data.intent === "prefix") {
      var prefixPanel = self.ensurePanel();
      prefixPanel.innerHTML = prefixSuggestHtml(data.suggestions || []) + viewAllButtonHtml(q);
      self.showPanel();
      prefixPanel.querySelectorAll(".sfs-prefix-chips a").forEach(function (a) {
        a.addEventListener("click", function () {
          recordRecentSearch(q);
        });
      });
      return;
    }

    if (!products.length) {
      dlog("zero paint deferred", q);
      this.zeroTimer = setTimeout(function () {
        self.zeroTimer = null;
        if (!signal.aborted && token === self.reqToken && self.input.value.trim() === q) {
          try { beaconPaintDiag = diag; } catch (eDiag) {}
          sendBeacon(q, "zero", 0);
          var panel = self.ensurePanel();
          // Wrapped in .predictive__search-result (native's own wrapper,
          // sections/predictive-search.liquid lines 2-3) even though there
          // are no products -- native does exactly this: the rail (queries/
          // pages/articles) still renders inside that wrapper on a
          // zero-product query, and .predictive__search--no-result /
          // .predictive_search-button both stay OUTSIDE it as siblings
          // (liquid lines 193, 218). Round 8 (2026-08-20): without this
          // wrapper the rail had no .predictive__search-rail-triggered grid
          // to sit in, so it rendered full-width instead of as the native
          // narrow-left/products-right layout -- see the products branch
          // below for the same fix. .empty-hidden is native's own class too
          // (not invented here): it's what the existing
          // `.empty-hidden:empty ~ .predictive__search--no-result` /
          // `.empty-hidden:empty ~ .predictive_search-button` rules
          // (header.css ~1529, ~1526) key off to show the no-result message
          // only when the wrapper is truly empty (no rail either) and hide
          // it whenever the rail has content -- matching native instead of
          // always painting both.
          panel.innerHTML =
            '<div class="predictive__search-result empty-hidden">' +
            suggestionRailHtml(data.suggestions || []) +
            progressiveDropdownSummaryHtml(progressive) +
            "</div>" +
            noResultHtml(q) +
            viewAllButtonHtml(q);
          self.showPanel();
          wireRailRecent(panel, q);
          dlog("zero paint committed", q);
        }
      }, ZERO_SETTLE_MS);
      return;
    }

    try { beaconPaintDiag = diag; } catch (eDiag) {}
    sendBeacon(q, "dropdown", products.length);

    var listHtml = progressive ? progressiveDropdownProductsHtml(products, progressive) : products.map(renderDropdownProductRow).join("");
    var panel = self.ensurePanel();
    // Same native wrapper as the zero branch above: .predictive__search-
    // result (sections/predictive-search.liquid lines 2-3) around
    // [rail, products group] only -- .predictive_search-button stays a
    // sibling outside it (liquid line 193). header.css's grid
    // (~1383-1421) activates on THIS wrapper only when it :has() a
    // .predictive__search-rail child, putting the rail in the 170px left
    // column and .predictive-search-products in the remaining column to
    // its right; without the wrapper (round 7 and earlier) that grid
    // never engaged and both painted full-width, stacked. .empty-hidden
    // is native's class, reused verbatim, not invented.
    panel.innerHTML =
      '<div class="predictive__search-result empty-hidden">' +
      suggestionRailHtml(data.suggestions || []) +
      '<div class="predictive__search-group predictive-search-products">' +
      "<h3>Products</h3>" +
      progressiveDropdownSummaryHtml(progressive) +
      '<ul role="group" aria-labelledby="predictive-search-products">' +
      listHtml +
      "</ul></div>" +
      "</div>" +
      viewAllButtonHtml(q);
    self.showPanel();
    wireRailRecent(panel, q);
    scheduleResultsPrerender(self.form, q);
    if (self.requestController && self.requestController.signal === signal) self.requestController = null;
    dlog("dropdown inserted", q, "parsed li count", panel.querySelectorAll('ul[aria-labelledby="predictive-search-products"] > li[data-product-id]').length);

    panel.querySelectorAll('ul[aria-labelledby="predictive-search-products"] > li[data-product-id]').forEach(function (li, idx) {
      li.addEventListener("click", function () {
        // Store the typed query when a product row is selected.
        recordRecentSearch(q);
        sendBeacon(q, "click", products.length, idx, li.getAttribute("data-product-id"));
      });
    });
  };

  function initDropdowns() {
    document.querySelectorAll("input.js-search-input").forEach(function (input) {
      new SfsDropdown(input);
    });
  }

  // ================= header/mobile search box: retain query + select-all on
  // refocus (2026-08-22 fix) =================
  // /a/search is an app-proxy page, so Shopify never populates the `search`
  // Liquid object there (see snippets/form.liquid's own `value="{{ search.
  // terms | escape }}"`) -- the box renders empty right after a shopper
  // searches even though the query is sitting in the URL's own `q` param.
  // Native /search is unaffected (Liquid already fills it there), so this
  // only ever acts when the box is still blank. Runs against every
  // input.js-search-input on the page -- same selector initDropdowns() uses
  // above, since both header.liquid and mobile-menu.liquid render their own
  // copy of snippets/form.liquid.

  function isSearchLandingPath() {
    var path = window.location.pathname;
    return path.indexOf("/a/search") === 0 || path.indexOf("/search") === 0;
  }

  // Results-page product clicks. Dropdown option rows keep their own
  // listener; this covers /a/search and /search cards, including after a
  // filter swap replaces the grid. One document listener, never per-card.
  var SEARCH_RESULTS_CLICK_DEDUPE_MS = 300;
  var searchResultsClickBound = false;

  function searchResultsClickQuery() {
    try {
      var q = (new URLSearchParams(window.location.search).get("q") || "").trim();
      if (q) return q;
      var input = document.querySelector("input.js-search-input");
      if (input && input.value) return String(input.value).trim();
      return "";
    } catch (e) {
      return "";
    }
  }

  function searchResultsReportedCount() {
    try {
      var el = document.querySelector("[data-search-results-total]");
      if (!el) return -1;
      var parsed = parseInt(el.getAttribute("data-search-results-total"), 10);
      return isFinite(parsed) ? parsed : -1;
    } catch (e) {
      return -1;
    }
  }

  function searchResultsCardList(card) {
    return card && card.closest
      ? card.closest(".list-products, .search-product-list, .search-results-body")
      : null;
  }

  function searchResultsCardPosition(list, card) {
    try {
      if (!list || !list.querySelectorAll) return 0;
      var cards = list.querySelectorAll(".product-card");
      for (var i = 0; i < cards.length; i++) {
        if (cards[i] === card) return i;
      }
      return 0;
    } catch (e) {
      return 0;
    }
  }

  function onSearchResultsCardActivate(event) {
    try {
      if (!event) return;
      if (typeof event.button === "number" && event.button > 1) return;
      if (!isSearchLandingPath()) return;
      var target = event.target;
      if (target && target.nodeType === 3) target = target.parentElement;
      if (!target || !target.closest) return;
      var card = target.closest(".product-card");
      if (!card) return;
      var list = searchResultsCardList(card);
      if (!list) return;
      var now = Date.now();
      if (card.__sfsClickBeaconAt && now - card.__sfsClickBeaconAt < SEARCH_RESULTS_CLICK_DEDUPE_MS) return;
      card.__sfsClickBeaconAt = now;
      sendBeacon(
        searchResultsClickQuery(),
        "click",
        searchResultsReportedCount(),
        searchResultsCardPosition(list, card),
        card.getAttribute("data-product-id") || null
      );
    } catch (e) {
      // Fire-and-forget. A results click must never break navigation.
    }
  }

  function initSearchResultsClickBeacon() {
    try {
      if (searchResultsClickBound) return;
      searchResultsClickBound = true;
      document.addEventListener("click", onSearchResultsCardActivate, true);
      document.addEventListener("auxclick", onSearchResultsCardActivate, true);
    } catch (e) {
      // never block boot
    }
  }

  function hydrateSearchInputFromQueryParam(input) {
    if (input.value) return; // Liquid already won (native /search) -- never override a real value
    var q = new URLSearchParams(window.location.search).get("q");
    if (!q) return;
    input.value = q;
    var field = input.closest(".form-field");
    var clearBtn = field && field.querySelector(".search__clear");
    if (clearBtn) clearBtn.removeAttribute("hidden");
  }

  function initSearchInputRetain() {
    try {
      if (!isSearchLandingPath()) return;
      document.querySelectorAll("input.js-search-input").forEach(function (input) {
        hydrateSearchInputFromQueryParam(input);
      });
    } catch (e) {
      // never let a cosmetic hydration miss block the rest of boot()
    }
  }

  function bindSelectAllOnFocus(input) {
    var justSelected = false;
    input.addEventListener("focus", function () {
      if (!input.value) return; // nothing to select, don't fight an empty box
      input.select();
      justSelected = true;
    });
    input.addEventListener("mouseup", function (event) {
      if (!justSelected) return;
      justSelected = false;
      event.preventDefault();
    });
    input.addEventListener("blur", function () {
      justSelected = false;
    });
  }

  function initSelectAllOnFocus() {
    try {
      document.querySelectorAll("input.js-search-input").forEach(function (input) {
        bindSelectAllOnFocus(input);
      });
    } catch (e) {
      // degrade to today's behavior (no select-all) rather than break boot()
    }
  }

  function isMobileSearchViewport() {
    return !!(window.matchMedia && window.matchMedia("(max-width: 991px)").matches);
  }

  function bindSearchClearButton(input) {
    var field = input.closest(".form-field");
    var clearBtn = field && field.querySelector(".search__clear");
    if (!clearBtn) return;
    if (clearBtn.getAttribute("data-sfs-clear") === "1") return; // already bound
    clearBtn.setAttribute("data-sfs-clear", "1");

    clearBtn.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      input.value = "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
      if (isMobileSearchViewport() && input.sfsDropdown) {
        input.sfsDropdown.dismiss();
        return;
      }
      clearBtn.setAttribute("hidden", "");
      input.focus();
    });

    input.addEventListener("input", function () {
      if (input.value || (isMobileSearchViewport() && input.getAttribute("aria-expanded") === "true")) clearBtn.removeAttribute("hidden");
      else clearBtn.setAttribute("hidden", "");
    });
  }

  function initSearchClearButtons() {
    try {
      document.querySelectorAll("input.js-search-input").forEach(function (input) {
        bindSearchClearButton(input);
      });
    } catch (e) {
      // never let a cosmetic clear-button miss block the rest of boot()
    }
  }

  function publicFilterUrl(rawHref, baseHref) {
    try {
      var base = new URL(baseHref || "https://superbreakers.com/");
      var url = new URL(rawHref, base);
      if (
        url.origin !== base.origin ||
        (url.pathname !== "/a/search" && url.pathname !== "/a/search/")
      ) return rawHref;
      url.searchParams.set("sfs_filters", "1");
      url.searchParams.delete("sb_brand_preview");
      url.searchParams.delete("sb_brand_preview_token");
      return url.pathname + (url.search ? url.search : "") + (url.hash ? url.hash : "");
    } catch (e) {
      return rawHref;
    }
  }

  function repairProxyBrandFacetHref(rawHref, baseHref) {
    try {
      var base = new URL(baseHref || "https://superbreakers.com/");
      var url = new URL(rawHref, base);
      if (
        url.origin !== base.origin ||
        (url.pathname !== "/a/search" && url.pathname !== "/a/search/")
      ) return rawHref;
      var facets = url.searchParams.get("facets") || "";
      if (!/^\s*\"brand_canonical\"\s*:\s*\[/.test(facets)) return rawHref;
      var repaired = "{" + facets + "}";
      var parsed = JSON.parse(repaired);
      if (!parsed || !Array.isArray(parsed.brand_canonical)) return rawHref;
      url.searchParams.set("facets", repaired);
      return url.pathname + (url.search ? url.search : "") + (url.hash ? url.hash : "");
    } catch (e) {
      return rawHref;
    }
  }

  function publicFiltersEnabled() {
    return document.documentElement.getAttribute("data-sb-brand-preview") === "true";
  }

  function preparePublicFilterHref(rawHref) {
    var repaired = repairProxyBrandFacetHref(rawHref, window.location.href);
    return publicFilterUrl(repaired, window.location.href);
  }

  function initPublicFilterRouting() {
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) return false;
    if (!publicFiltersEnabled()) return false;

    function ensureHiddenInput(form, name, value) {
      var input = form.querySelector('input[name="' + name + '"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    }

    document.querySelectorAll('form[action^="/a/search"]').forEach(function (form) {
      ensureHiddenInput(form, "sfs_filters", "1");
    });

    document.addEventListener("submit", function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[action^="/a/search"]')) return;
      ensureHiddenInput(form, "sfs_filters", "1");
    }, true);

    document.addEventListener("click", function (event) {
      var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!link) return;
      var next = preparePublicFilterHref(link.getAttribute("href") || "");
      if (next !== link.getAttribute("href")) link.setAttribute("href", next);
    }, true);

    document.querySelectorAll('a[href]').forEach(function (link) {
      var currentHref = link.getAttribute("href") || "";
      var nextHref = preparePublicFilterHref(currentHref);
      if (nextHref !== currentHref) link.setAttribute("href", nextHref);
    });

    // The backend renders the public filter contract on the first response.
    // Keep repairing future forms and links, but never reload a rendered page.
    return false;
  }

  // ================= bootstrap =================

  function initProxyBusyFallback() {
    if (typeof window !== "undefined" && window.SFSNativeFallback && window.SFSNativeFallback.enabled()) {
      return false;
    }
    if (!isProxySearchPath(window.location.pathname)) return false;
    var root = document.getElementById("shopify-section-proxy-search");
    if (root && proxyPageHasStableResultStructure(root)) return false;

    var destination = nativeFallbackHrefFromProxy(window.location.href, window.location.href);
    if (destination === window.location.href) return false;
    window.location.replace(destination);
    return true;
  }

  // Only stable proxy pages enter history.
  function recordRecentSearchOnProxyLanding() {
    if (!isProxySearchPath(window.location.pathname)) return;
    var root = document.getElementById("shopify-section-proxy-search");
    if (!proxyPageHasStableResultStructure(root)) return;
    var sp = new URLSearchParams(window.location.search);
    if (sp.get("sfs_native") === "1") return;
    var q = (sp.get("q") || "").trim();
    if (!q) return;
    recordRecentSearch(q);
  }

  function setProxySearchPageTitle() {
    if (!isProxySearchPath(window.location.pathname)) return;
    var q = (new URLSearchParams(window.location.search).get("q") || "").trim();
    if (!q) return;
    document.title = "Search results for " + q + " | SuperBreakers";
  }

  // The document-head bootstrap normalizes the server markup before first
  // paint. Reuse that implementation here so later search refreshes keep the
  // same descriptive H1 and collection-style result count.
  function normalizeProxySearchHeading() {
    var normalize = window.__sbNormalizeProxySearchHeading;
    return typeof normalize === "function" ? normalize() : false;
  }

  function initProxySearchHeadingNormalization() {
    if (!isProxySearchPath(window.location.pathname)) return;
    var root = document.getElementById("shopify-section-proxy-search");
    if (!root) return;
    normalizeProxySearchHeading();
    if (root.__sfsHeadingObserver) return;
    root.__sfsHeadingObserver = new MutationObserver(normalizeProxySearchHeading);
    root.__sfsHeadingObserver.observe(root, { childList: true, subtree: true });
  }

  // Category-style groups are permanently hidden via CSS (plp-shell.css) and
  // never count toward the "first three" -- an offsetParent/visibility check
  // instead would call every group hidden while the mobile drawer itself is
  // closed, so eligibility is decided from the semantic group class alone.
  var INELIGIBLE_DISCLOSURE_GROUPS = {
    category: true,
    "product-category": true,
    "breaker-category": true
  };

  function isEligibleDisclosureGroup(filterBlock) {
    if (!filterBlock || !filterBlock.classList) return true;
    for (var i = 0; i < filterBlock.classList.length; i += 1) {
      if (INELIGIBLE_DISCLOSURE_GROUPS[filterBlock.classList[i]]) return false;
    }
    return true;
  }

  // The proxy service now SSR-renders data-sfs-disclosure-ready on every
  // facet detail with the first three eligible facets already open, so this
  // only has real initialization work left to do for non-SSR swaps. Match
  // the collection rail by leaving the first three eligible facets expanded
  // and later facets collapsed. Sort remains independent so the mobile
  // Filter & Sort drawer keeps its normal control. Details already marked
  // ready keep whatever open state a prior run or a shopper's own toggle
  // left them in; only unready details are initialized here, though an
  // already-ready eligible detail still advances the eligible-group count
  // so a later pass can't reopen a fourth group.
  function initProxyFilterDisclosures() {
    if (window.location.pathname !== "/a/search" && window.location.pathname !== "/a/search/") return;
    var details = document.querySelectorAll(
      "#shopify-section-proxy-search .filter-block > details"
    );
    var eligiblePosition = 0;
    Array.prototype.forEach.call(details, function (detail) {
      var eligible = isEligibleDisclosureGroup(detail.parentElement);
      if (detail.hasAttribute("data-sfs-disclosure-ready")) {
        if (eligible) eligiblePosition += 1;
        return;
      }
      if (eligible) {
        detail.open = eligiblePosition < 3;
        eligiblePosition += 1;
      } else {
        detail.open = false;
      }
      detail.setAttribute("data-sfs-disclosure-ready", "");
    });
  }

  // The proxy already ranks an exact catalog match first, but it does not
  // render the class used by the theme's established Exact Match card style.
  // Match the visible card SKU locally so the treatment survives direct URLs
  // as well as dropdown commits. Spacing and punctuation are ignored, a
  // visible brand prefix is accepted, and only a terminal "-R" is treated as
  // a recertified-condition suffix.
  function proxyCardMatchesExactQuery(card, rawQuery) {
    function stripRecertSuffix(value) {
      return String(value == null ? "" : value).trim().replace(/(?:[-_\s]+R)$/i, "");
    }

    function exactKey(value) {
      return stripRecertSuffix(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
    }

    var queryKey = exactKey(rawQuery);
    var skuKey = exactKey(card.getAttribute("data-sku") || "");
    if (!queryKey || !skuKey) return false;
    if (queryKey === skuKey) return true;

    var brandElement = card.querySelector(".product-card-title-brand");
    var brand = brandElement ? brandElement.textContent.trim() : "";
    if (!brand) return false;

    var brandParts = brand.split(/\s*(?:\/|&|\|)\s*/);
    brandParts.push(brand);
    for (var i = 0; i < brandParts.length; i += 1) {
      var brandKey = exactKey(brandParts[i]);
      if (!brandKey || queryKey.indexOf(brandKey) !== 0) continue;
      if (queryKey.slice(brandKey.length) === skuKey) return true;
    }
    return false;
  }

  function initProxyExactMatchHighlight() {
    if (!isProxySearchPath(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var query = (params.get("q") || "").trim();
    if (!query) return;

    var highlightedProductId = params.get("highlight_product") || "";
    var cards = document.querySelectorAll(
      "#shopify-section-proxy-search .list-products .product-card"
    );
    var exactCard = null;

    Array.prototype.forEach.call(cards, function (card) {
      card.classList.remove("exact-match-highlight");
      if (exactCard) return;

      var productIdMatches = highlightedProductId
        && String(card.getAttribute("data-product-id") || "") === highlightedProductId;
      if (productIdMatches || proxyCardMatchesExactQuery(card, query)) exactCard = card;
    });

    if (exactCard) exactCard.classList.add("exact-match-highlight");
  }

  function initProxyZeroStateRetry() {
    if (!isProxySearchPath(window.location.pathname)) return;

    var emptyState = document.querySelector("#shopify-section-proxy-search .sfs-empty");
    if (!emptyState) return;
    if (emptyState.dataset.homeCatalogReady === "true") return;

    var owner = document.querySelector('script[src*="search-client.js"]');
    if (!owner) return;

    var configuredAsset = window.theme && window.theme.assets
      ? window.theme.assets.searchEmptyHomeCatalogScript
      : "";
    var assetUrl = new URL(configuredAsset || owner.src, window.location.href);
    if (!configuredAsset) {
      assetUrl.pathname = assetUrl.pathname.replace(/search-client\.js$/, "search-empty-home-catalog.js");
      assetUrl.search = "";
    }

    var script = document.createElement("script");
    script.src = assetUrl.href;
    script.async = true;
    script.dataset.sfsEmptyCatalog = "true";
    document.head.appendChild(script);
  }

  function boot() {
    // The native form remains usable if the helper asset fails.
    // Do not revive the legacy proxy handoff or dropdown owner in that case.
    if (typeof window !== 'undefined' && window.SFS_NATIVE_FALLBACK &&
        window.SFS_NATIVE_FALLBACK.enabled === true &&
        !window.SFSNativeFallback) return;
    // Set the disclosure state before the rest of search-page boot work.
    initProxyFilterDisclosures();
    if (initPublicFilterRouting()) return;
    if (initProxyBusyFallback()) return;
    ensureUniqueProxyFilterControlIds();
    initLegacySearchHandoff();
    if (engineSuggestEnabled()) {
      initDropdowns();
      if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(warmSuggestPath);
      else setTimeout(warmSuggestPath, 1500);
    }
    recordRecentSearchOnProxyLanding();
    setProxySearchPageTitle();
    initProxySearchHeadingNormalization();
    initProxyExactMatchHighlight();
    initProxyZeroStateRetry();
    initSearchInputRetain();
    initSearchClearButtons();
    initSelectAllOnFocus();
    initSearchResultsClickBeacon();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
